import { TeamRufnummern } from "@/components/portal/TeamRufnummern"
import { WaFunktionen, type WaFunktionGruppe } from "@/components/portal/WaFunktionen"
import { nummerKurz, textZuZeilen } from "@/lib/teamRufnummern"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState, useRef } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, Save, Trash2 } from "lucide-react"
import { api } from "@/lib/api"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { cn } from "@/lib/utils"

/**
 * Tenant-Edit — Kernfelder im React-Portal, Advanced-Config im alten.
 * Das alte Formular hat 40+ Felder (Booking-Config, Smart-Inbox-Rules,
 * Project-Types, Monteure, Signatur, Forwarding-Rules). Das wuerde 1000+
 * Zeilen React-Code. Pragmatischer Ansatz:
 *  - Core-Felder hier (Name/Kunde/IMAP/HERO/Sync)
 *  - Advanced-Sektionen verlinken aufs alte Portal (nach oben fixiert)
 */
type BookingConfig = {
  default_slot_duration_min: number
  lead_time_hours: number
  business_hour_start: number
  business_hour_end: number
  business_hours_by_day: Record<string, { start: number; end: number }>
  lunch_break_enabled: boolean
  lunch_break_start: number
  lunch_break_end: number
  search_days: number
  max_alt: number
  working_days: number[]
  slot_step_min: number
  relevant_partner_ids: number[]
  main_partner_id: number | null
  allday_squeeze_enabled: boolean
  allday_squeeze_min_days: number
  allday_squeeze_hours: number[]
  blackout_periods: BlackoutPeriod[]
  max_appointments_per_day: number
  /** #102: Termin beim Kunden als Zeitfenster ab Startzeit; 0 = exakte Uhrzeit. */
  ankunftsfenster_min: number
  /** voice#35: wie der Termin genannt wird — "zwischen" | "ab" | "um"; "" = wie bisher
   *  (mit Fenster „zwischen", sonst „um"). Aufgeloest in services/zeitfenster.py. */
  termin_formulierung: '' | 'zwischen' | 'ab' | 'um'
  /** #113: HERO-Kategorien (Namen), die die Terminsuche nicht als belegt zaehlt. */
  info_kategorien: string[]
  /** voice#16: Kategorien, die „Kollege ist nicht da" bedeuten (Hinweis in der Rueckruf-Aufgabe). */
  abwesenheit_kategorien: string[]
  /** #117: PLZ oder PLZ-Anfaenge des Einzugsgebiets; leer = keine Pruefung. */
  einzugsgebiet_plz: string[]
  /** #134: wiederkehrende Wochenfenster ohne Auto-Buchung (0=Mo..6=So). */
  sperrzeiten_woechentlich: WochenSperre[]
  /** #134: Regeln nur fuer Besichtigungen. */
  besichtigung_regel: BesichtigungRegel
  /** #147: Wartung direkt als Termin (keine Besichtigung) + Typennummer erfragen. */
  wartung_regel: WartungRegel
}

type WartungRegel = { aktiv: boolean; stichworte: string[] }

type WochenSperre = { tag: number; von: number; bis: number; grund: string }

type BesichtigungRegel = {
  aktiv: boolean
  tage: number[]
  ab_stunde: number
  bevorzugt_tage_voraus: number
  max_pro_tag_sonst: number
  stichworte: string[]
}

// Sperrzeitraum. start/end sind ISO-Strings in Berliner Zeit; nur-Datum
// ("2026-12-24") wird vom Backend inklusiv gelesen — der End-Tag ist also
// komplett gesperrt.
type BlackoutPeriod = {
  start: string
  end: string
  reason: string
}

type OfficeHours = {
  start: string
  end: string
  days: number[]
}

type SipgateChannel = {
  number: string
  channel_name: string
}

// fortytools-Provider-Config. client_secret wird beim Laden NIE befuellt
// (Backend liefert nur ein Set-Flag); nur bei Neueingabe gesendet.
type FortytoolsForm = {
  client_id: string
  client_secret: string
  staff_member_id: number | null
  customer_state_id: number | null
  shadow: boolean
  capacity: number
}

type FormState = {
  name: string
  kunden_slug: string
  email: string
  imap_host: string
  imap_port: number
  imap_user: string
  imap_password: string
  imap_folder: string
  imap_auth_type: string
  sync_all_folders: boolean
  ms_tenant_id: string
  ms_client_id: string
  ms_client_secret: string
  hero_token: string
  hero_ical_url: string
  meiti_ical_url: string
  monteur_email: string
  signature_html: string
  sms_absender: string
  sms_aktiv: boolean
  sms_template: string
  sms_nur_neukunden: boolean
  sms_ohne_buchung: boolean
  whatsapp_aktiv: boolean
  kanal_kunde_abgleich: Kanal
  kanal_kunde_terminlink: Kanal
  kanal_betrieb_meldung: Kanal
  kanal_betrieb_notfall: Kanal
  wa_aufgaben_vorschlaege: boolean
  wa_angebote: boolean
  inhaber_fragen: boolean
  wa_mail_nummern: string[]
  /** HERO-Dokumenttyp (ID oder Name) fuer WhatsApp-Arbeitsberichte, "" = Standard (Arman 23.10.2026) */
  wa_bericht_dokumenttyp: string
  /** Was der WhatsApp-Assistent darf: Funktion → an/aus (Paul 23.10.2026, `services/whatsapp_funktionen.py`) */
  wa_funktionen: Record<string, boolean>
  terminlink_aktiv: boolean
  sms_api_key: string
  hero_default_author_user_id: number | null
  hero_calendar_category_id: number | null
  booking: BookingConfig
  office_hours: OfficeHours
  telefon_namen: string
  sipgate_channels: SipgateChannel[]
  backend_provider: string
  fortytools: FortytoolsForm
  branche: string
}

const DEFAULT_FORTYTOOLS: FortytoolsForm = {
  client_id: "", client_secret: "",
  staff_member_id: null, customer_state_id: null,
  shadow: true, capacity: 1,
}

const DEFAULT_BOOKING: BookingConfig = {
  default_slot_duration_min: 120, lead_time_hours: 24,
  business_hour_start: 7, business_hour_end: 18,
  business_hours_by_day: {},
  lunch_break_enabled: false, lunch_break_start: 12, lunch_break_end: 13,
  search_days: 14, max_alt: 3,
  // 0=Mo..6=So (siehe WEEKDAYS_BOOKING) — [1..5] hiess Di–Sa und lieferte
  // Samstags-Termine (voice#24, test_tenant 18.09.2026).
  working_days: [0, 1, 2, 3, 4], slot_step_min: 60,
  relevant_partner_ids: [],
  main_partner_id: null,
  allday_squeeze_enabled: false,
  allday_squeeze_min_days: 2,
  allday_squeeze_hours: [8, 15],
  blackout_periods: [],
  max_appointments_per_day: 0,
  ankunftsfenster_min: 0,
  termin_formulierung: '',
  info_kategorien: ["Information"],
  abwesenheit_kategorien: ["Urlaub", "Abwesenheit", "Krank", "Frei"],
  einzugsgebiet_plz: [],
  sperrzeiten_woechentlich: [],
  besichtigung_regel: {
    aktiv: false, tage: [3], ab_stunde: 10, bevorzugt_tage_voraus: 7, max_pro_tag_sonst: 1,
    stichworte: ["besichtig", "kostenvoranschlag", "aufmaß", "aufmass", "angebot"],
  },
  wartung_regel: { aktiv: false, stichworte: ["wartung"] },
}

const DEFAULT_OFFICE_HOURS: OfficeHours = { start: "", end: "", days: [] }

const EMPTY: FormState = {
  name: "", kunden_slug: "", email: "",
  imap_host: "", imap_port: 993, imap_user: "", imap_password: "", imap_folder: "INBOX",
  imap_auth_type: "password", ms_tenant_id: "", ms_client_id: "", ms_client_secret: "",
  sync_all_folders: true,
  hero_token: "", hero_ical_url: "", meiti_ical_url: "",
  monteur_email: "",
  signature_html: "", sms_absender: "", sms_aktiv: false, sms_template: "", sms_nur_neukunden: false,
  sms_ohne_buchung: false, whatsapp_aktiv: false, terminlink_aktiv: false,
  kanal_kunde_abgleich: "aus", kanal_kunde_terminlink: "aus", kanal_betrieb_meldung: "sms",
  kanal_betrieb_notfall: "sms", wa_aufgaben_vorschlaege: false, wa_angebote: false, inhaber_fragen: false, wa_mail_nummern: [],
  wa_bericht_dokumenttyp: "", wa_funktionen: {},
  sms_api_key: "",
  hero_default_author_user_id: null,
  hero_calendar_category_id: null,
  booking: DEFAULT_BOOKING,
  office_hours: DEFAULT_OFFICE_HOURS,
  telefon_namen: "",
  sipgate_channels: [],
  backend_provider: "hero",
  fortytools: DEFAULT_FORTYTOOLS,
  branche: "shk",
}

// Convention: 0=So..6=Sa (Python isoweekday() % 7). Genutzt fuer office_hours (Sipgate-Ticket).
const WEEKDAYS: { id: number; label: string }[] = [
  { id: 1, label: "Mo" }, { id: 2, label: "Di" }, { id: 3, label: "Mi" },
  { id: 4, label: "Do" }, { id: 5, label: "Fr" }, { id: 6, label: "Sa" }, { id: 0, label: "So" },
]

// Convention: 0=Mo..6=So (Python weekday()). Genutzt fuer booking_config.working_days.
const WEEKDAYS_BOOKING: { id: number; label: string }[] = [
  { id: 0, label: "Mo" }, { id: 1, label: "Di" }, { id: 2, label: "Mi" },
  { id: 3, label: "Do" }, { id: 4, label: "Fr" }, { id: 5, label: "Sa" }, { id: 6, label: "So" },
]

export default function TenantEdit() {
  const { slug } = useParams<{ slug: string }>()
  const qc = useQueryClient()
  const [form, setForm] = useState<FormState>(EMPTY)
  const sigRef = useRef<HTMLTextAreaElement>(null)

  /** Setzt einen Bild-Platzhalter an der Cursorposition ein (sonst am Ende).
   *  Abtippen waere fehleranfaellig: ein Tippfehler im Namen faellt erst beim
   *  Empfaenger auf, weil der Platzhalter dann leer bleibt. */
  const platzhalterEinfuegen = (text: string) => {
    const el = sigRef.current
    const aktuell = form.signature_html || ""
    if (!el) { setForm({ ...form, signature_html: aktuell + text }); return }
    const a = el.selectionStart ?? aktuell.length
    const b = el.selectionEnd ?? aktuell.length
    const neu = aktuell.slice(0, a) + text + aktuell.slice(b)
    setForm({ ...form, signature_html: neu })
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(a + text.length, a + text.length)
    })
  }
  const [heroTokenMasked, setHeroTokenMasked] = useState(false)
  const [imapPwMasked, setImapPwMasked] = useState(false)
  const [msSecretMasked, setMsSecretMasked] = useState(false)
  const [mailTest, setMailTest] = useState<null | { running?: boolean; res?: any }>(null)
  const [smsTest, setSmsTest] = useState<null | { running?: boolean; res?: any }>(null)
  const [fortySecretMasked, setFortySecretMasked] = useState(false)
  const [saveOk, setSaveOk] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ["tenant", slug],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/tenants/{slug}", {
        params: { path: { slug: slug! } },
      })
      if (error) throw new Error(String(error))
      return data
    },
    enabled: !!slug,
  })

  // Kalender-Kategorien des Mandanten — damit die Auswahl Namen zeigt statt
  // einer nackten Zahl. Ein Zahlenfeld hat am 18.08. zu 438890 statt 438892
  // gefuehrt: HERO kannte die ID nicht, jede automatische Buchung waere an
  // "Record not found in calendar_event_categories" gescheitert.
  const { data: kategorienData } = useQuery({
    queryKey: ["tenant-kalender-kategorien", slug],
    enabled: !!slug,
    queryFn: async () => {
      const res = await fetch(`/api/v1/calendar/options?tenant=${slug}`, { credentials: "include" })
      if (!res.ok) return { categories: [] as { id: number; name: string }[] }
      return (await res.json()) as { categories: { id: number; name: string }[] }
    },
  })

  // Dokumenttypen des Betriebs live aus HERO — fuer den Typ der WhatsApp-Arbeitsberichte
  // (Arman 23.10.2026: Sanitec hat einen eigenen). Fehler = Hinweis statt leerer Liste.
  const { data: dokumenttypenData, isError: dokumenttypenFehler } = useQuery({
    queryKey: ["tenant-hero-dokumenttypen", slug],
    enabled: !!slug,
    retry: false,
    queryFn: async () => {
      const res = await fetch(`/api/v1/tenants/${encodeURIComponent(slug!)}/hero-dokumenttypen`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return (await res.json()) as { typen: { id: number; name: string; base_type: string }[] }
    },
  })

  const { data: monteureData } = useQuery({
    queryKey: ["tenant-monteure", slug],
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/calendar/monteure", {
        params: { query: { tenant: slug! } },
      })
      if (error) throw new Error(String(error))
      return data
    },
    enabled: !!slug,
  })

  useEffect(() => {
    if (data) {
      setForm({
        name: data.name,
        kunden_slug: data.kunden_slug || "",
        email: data.email || "",
        imap_host: data.imap_host || "",
        imap_port: data.imap_port,
        imap_user: data.imap_user || "",
        imap_password: "",
        imap_folder: data.imap_folder || "INBOX",
        imap_auth_type: (data as any).imap_auth_type || "password",
        sync_all_folders: (data as any).sync_all_folders !== false,
        ms_tenant_id: (data as any).ms_tenant_id || "",
        ms_client_id: (data as any).ms_client_id || "",
        ms_client_secret: "",
        hero_token: "",
        hero_ical_url: data.hero_ical_url || "",
        meiti_ical_url: data.meiti_ical_url || "",
        monteur_email: data.monteur_email || "",
        signature_html: data.signature_html || "",
        sms_absender: (data as any).sms_absender || "",
        sms_aktiv: !!(data as any).sms_aktiv,
        sms_template: (data as any).sms_template || "",
        sms_nur_neukunden: !!(data as any).sms_nur_neukunden,
        sms_ohne_buchung: !!(data as any).sms_ohne_buchung,
        whatsapp_aktiv: !!(data as any).whatsapp_aktiv,
        kanal_kunde_abgleich: kanalWert((data as any).kanal_kunde_abgleich, "aus"),
        kanal_kunde_terminlink: kanalWert((data as any).kanal_kunde_terminlink, "aus"),
        kanal_betrieb_meldung: kanalWert((data as any).kanal_betrieb_meldung, "sms"),
        kanal_betrieb_notfall: kanalWert((data as any).kanal_betrieb_notfall, "sms"),
        wa_aufgaben_vorschlaege: !!(data as any).wa_aufgaben_vorschlaege,
        wa_angebote: !!(data as any).wa_angebote,
        inhaber_fragen: !!(data as any).inhaber_fragen,
        wa_mail_nummern: (data as any).wa_mail_nummern || [],
        wa_bericht_dokumenttyp: (data as any).wa_bericht_dokumenttyp || "",
        wa_funktionen: (data as any).wa_funktionen || {},
        terminlink_aktiv: !!(data as any).terminlink_aktiv,
        sms_api_key: "",   // Secret: kommt nie vom Server, leer = unveraendert
        hero_default_author_user_id: (data as any).hero_default_author_user_id ?? null,
        hero_calendar_category_id: (data as any).hero_calendar_category_id ?? null,
        // `booking_config` liegt serverseitig als freies JSON in der Spalte, die
        // API beschreibt es entsprechend unspezifisch (business_hours_by_day als
        // dict-von-dict-von-int). Unser BookingConfig ist praeziser — der Cast
        // markiert genau die Stelle, an der aus losem JSON ein Typ wird.
        booking: { ...DEFAULT_BOOKING, ...((data.booking_config || {}) as Partial<BookingConfig>) },
        telefon_namen: (data as any).telefon_namen || "",
        office_hours: {
          start: (data as any).office_hours?.start || "",
          end: (data as any).office_hours?.end || "",
          days: (data as any).office_hours?.days || [],
        },
        sipgate_channels: ((data as any).sipgate_channels || []).map((c: any) => ({
          number: c.number,
          channel_name: c.channel_name,
        })),
        backend_provider: (data as any).backend_provider || "hero",
        branche: (data as any).branche || "shk",
        fortytools: {
          client_id: (data as any).fortytools?.client_id || "",
          client_secret: "",
          staff_member_id: (data as any).fortytools?.staff_member_id ?? null,
          customer_state_id: (data as any).fortytools?.customer_state_id ?? null,
          shadow: (data as any).fortytools?.shadow ?? true,
          capacity: (data as any).fortytools?.capacity ?? 1,
        },
      })
      setHeroTokenMasked(data.hero_token_set)
      setImapPwMasked(data.imap_password_set)
      setMsSecretMasked((data as any).ms_client_secret_set || false)
      setFortySecretMasked((data as any).fortytools?.client_secret_set || false)
    }
  }, [data])

  const save = useMutation({
    mutationFn: async () => {
      // Nur gesetzte/geänderte Felder senden
      const body: any = { ...form, booking_config: form.booking }
      delete body.booking
      // Mail-Haken nur fuer Nummern, die noch in der Team-Liste stehen
      const team = textZuZeilen(form.telefon_namen).map((z) => nummerKurz(z.nummer))
      body.wa_mail_nummern = form.wa_mail_nummern.filter((k) => team.includes(k))
      // Ohne geladenen Katalog nichts schicken — {} wuerde alle Funktionen wieder einschalten
      if (!Object.keys(form.wa_funktionen).length) delete body.wa_funktionen
      // Secrets: nur senden wenn User was eingegeben hat
      if (!form.hero_token) delete body.hero_token
      if (!form.imap_password) delete body.imap_password
      if (!form.ms_client_secret) delete body.ms_client_secret
      if (!form.sms_api_key) delete body.sms_api_key
      // fortytools nur schreiben wenn dieses Backend gewaehlt ist; client_secret
      // nur senden wenn neu eingegeben (sonst wuerde "" das Secret loeschen).
      if (form.backend_provider === "fortytools") {
        body.fortytools = { ...form.fortytools }
        if (!form.fortytools.client_secret) delete body.fortytools.client_secret
      } else {
        delete body.fortytools
      }
      const { error } = await api.PATCH("/api/v1/tenants/{slug}", {
        params: { path: { slug: slug! } },
        body,
      })
      if (error) throw new Error(String(error))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant", slug] })
      qc.invalidateQueries({ queryKey: ["tenants-list"] })
      qc.invalidateQueries({ queryKey: ["tenants-dropdown"] })
      setSaveOk(true)
      setTimeout(() => setSaveOk(false), 3000)
    },
  })

  if (isLoading) return <Loading label="Lädt Tenant..." />
  if (error) return <div className="p-10"><ErrorBox error={error} /></div>
  if (!data) return null

  const testTenant = async () => {
    try {
      const r = await fetch(`/admin/tenant/${data.slug}/test`, { method: "POST", credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      alert("Test-Aufruf ausgeführt. Siehe Logs für Details.")
    } catch (e) {
      alert("Test fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)))
    }
  }

  return (
    <div className="px-10 py-8 max-w-[900px]">
      <div className="flex items-center justify-between mb-4">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>
      </div>

      <form
        onSubmit={(ev) => { ev.preventDefault(); save.mutate() }}
        className="rounded-lg border bg-card overflow-hidden shadow-xs"
      >
        <div className="px-6 py-4 border-b bg-secondary flex items-center justify-between">
          <div>
            <h1 className="text-[20px] font-bold">{data.name || data.slug}</h1>
            <div className="text-[12px] text-muted-foreground mt-0.5">Slug: {data.slug}</div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/kunde/${data.slug}`}
              className="inline-flex items-center gap-1 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-success/10 text-success hover:bg-success/20"
            >
              Übersicht
            </Link>
            <button
              type="button"
              onClick={testTenant}
              className="inline-flex items-center gap-1 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20"
            >
              Test
            </button>
          </div>
        </div>

        <div className="p-6">
          <Section title="Allgemein" icon="⚙️" configured={!!form.name} defaultOpen>
            <F label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required /></F>
            <F label="Kunden-Slug"><input value={form.kunden_slug} onChange={(e) => setForm({ ...form, kunden_slug: e.target.value })} className={inputCls} /></F>
            <F label="Branche" hint="Steuert die Formulierung der KI-Analyse (E-Mail/Draft). Unabhängig vom Backend.">
              <select value={form.branche} onChange={(e) => setForm({ ...form, branche: e.target.value })} className={inputCls}>
                <option value="shk">SHK / Handwerk (Standard)</option>
                <option value="reinigung">Reinigung / Gebäudereinigung</option>
                <option value="holzbau">Holzbau / Zimmerei</option>
              </select>
            </F>
          </Section>

          <Section title="Backend-Provider" icon="🔌" configured={form.backend_provider === "fortytools"}>
            <F label="Terminsystem / Backend" hint="Welches System pflegt Kontakte, Projekte und Kalender? Standard: HERO.">
              <select
                value={form.backend_provider}
                onChange={(e) => setForm({ ...form, backend_provider: e.target.value })}
                className={inputCls}
              >
                <option value="hero">HERO (Standard)</option>
                <option value="fortytools">fortytools (zvoove)</option>
                <option value="keins">Keins – nur Telefonassistent</option>
              </select>
            </F>
            {form.backend_provider === "fortytools" && (
              <div className="mt-3 space-y-3 border-l-2 border-primary/30 pl-3">
                <p className="text-[12px] text-muted-foreground">
                  fortytools kann Termine nicht per API anlegen — Buchungen werden als Übergabe-Aufgabe erzeugt,
                  das Büro pflegt den Einsatz in fortytools nach. Freie Zeiten rechnen wir aus den gelesenen Einsätzen.
                </p>
                <F label="Client-ID (OAuth)">
                  <input
                    value={form.fortytools.client_id}
                    onChange={(e) => setForm({ ...form, fortytools: { ...form.fortytools, client_id: e.target.value } })}
                    className={inputCls}
                    placeholder="fortytools API Client-ID"
                  />
                </F>
                <F label="Client-Secret (OAuth)" hint={fortySecretMasked ? "Aktuell gesetzt. Nur bei Änderung neu eingeben." : "Noch nicht gesetzt."}>
                  <input
                    type="password"
                    placeholder={fortySecretMasked ? "••••••••" : "Client-Secret..."}
                    value={form.fortytools.client_secret}
                    onChange={(e) => setForm({ ...form, fortytools: { ...form.fortytools, client_secret: e.target.value } })}
                    className={inputCls}
                  />
                </F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Staff-Member-ID" hint="Standard-Monteur für Übergabe-Aufgaben">
                    <input
                      type="number"
                      value={form.fortytools.staff_member_id ?? ""}
                      onChange={(e) => setForm({ ...form, fortytools: { ...form.fortytools, staff_member_id: e.target.value === "" ? null : (parseInt(e.target.value) || null) } })}
                      className={inputCls}
                    />
                  </F>
                  <F label="Customer-State-ID" hint={'"Kunde" = 56346'}>
                    <input
                      type="number"
                      value={form.fortytools.customer_state_id ?? ""}
                      onChange={(e) => setForm({ ...form, fortytools: { ...form.fortytools, customer_state_id: e.target.value === "" ? null : (parseInt(e.target.value) || null) } })}
                      className={inputCls}
                    />
                  </F>
                </div>
                <F label="Kapazität (Fallback)" hint="Nur genutzt, wenn die Mitarbeiterliste (/staff_members) nicht lesbar ist. Sonst aus dem Pool abgeleitet.">
                  <input
                    type="number"
                    min={1}
                    value={form.fortytools.capacity}
                    onChange={(e) => setForm({ ...form, fortytools: { ...form.fortytools, capacity: parseInt(e.target.value) || 1 } })}
                    className={inputCls}
                  />
                </F>
                <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.fortytools.shadow}
                    onChange={(e) => setForm({ ...form, fortytools: { ...form.fortytools, shadow: e.target.checked } })}
                  />
                  <span>Shadow-Modus — Schreiben nur protokollieren, nichts wirklich in fortytools anlegen</span>
                </label>
              </div>
            )}
          </Section>

          <Section title="HERO Integration" icon="🔗" configured={!!(heroTokenMasked || form.hero_token)}>
            <F label="HERO-Token" hint={heroTokenMasked ? "Aktuell gesetzt. Nur bei Änderung neu eingeben." : "Noch nicht gesetzt."}>
              <input
                type="password"
                placeholder={heroTokenMasked ? "••••••••" : "HERO-API-Token..."}
                value={form.hero_token}
                onChange={(e) => setForm({ ...form, hero_token: e.target.value })}
                className={inputCls}
              />
            </F>
            <F label="HERO iCal-URL (optional, für externen Feed)">
              <input value={form.hero_ical_url} onChange={(e) => setForm({ ...form, hero_ical_url: e.target.value })} className={inputCls} />
            </F>
          </Section>

          <Section title="E-Mail (IMAP/SMTP)" icon="✉️" configured={!!(form.imap_host && form.imap_user)}>
            <F label="Eingehende E-Mails an diese Adresse" hint="E-Mails an diese Adresse werden diesem Tenant zugeordnet">
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="info@firma.de" />
            </F>
            <div className="grid grid-cols-3 gap-3">
              <F label="Host"><input value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} className={inputCls} placeholder="imap.gmail.com" /></F>
              <F label="Port"><input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value) || 993 })} className={inputCls} /></F>
              <F label="Ordner"><input value={form.imap_folder} onChange={(e) => setForm({ ...form, imap_folder: e.target.value })} className={inputCls} placeholder="INBOX" /></F>
            </div>
            <F label="User" hint="Bei OAuth2 die Postfachadresse (auch bei Shared Mailbox)">
              <input value={form.imap_user} onChange={(e) => setForm({ ...form, imap_user: e.target.value })} className={inputCls} />
            </F>
            <F label="Ordner-Umfang" hint="Postfächer mit großer Kundenablage: nur den Posteingang scannen. Eingehende Post landet dort ohnehin — und hunderte Ordner laufen bei Exchange in die Verbindungsdrosselung.">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.sync_all_folders}
                  onChange={(e) => setForm({ ...form, sync_all_folders: e.target.checked })}
                />
                Alle Ordner synchronisieren {form.sync_all_folders ? "" : "— aktuell nur INBOX, Gesendet und Entwürfe"}
              </label>
            </F>
            <F label="Anmeldeverfahren" hint="Microsoft 365 hat Basic Auth abgeschaltet — dort ist OAuth2 Pflicht.">
              <select
                value={form.imap_auth_type}
                onChange={(e) => setForm({ ...form, imap_auth_type: e.target.value })}
                className={inputCls}
              >
                <option value="password">Passwort (IONOS, Gmail, …)</option>
                <option value="oauth2_ms">OAuth2 — Microsoft 365</option>
              </select>
            </F>
            {form.imap_auth_type === "password" ? (
              <F label="Passwort" hint={imapPwMasked ? "Aktuell gesetzt. Nur bei Änderung neu eingeben." : "Noch nicht gesetzt."}>
                <input type="password" placeholder={imapPwMasked ? "••••••••" : ""} value={form.imap_password} onChange={(e) => setForm({ ...form, imap_password: e.target.value })} className={inputCls} />
              </F>
            ) : (
              <div className="space-y-3 rounded-md border border-dashed p-3">
                <div className="text-xs text-muted-foreground">
                  App-only-Zugriff (Client Credentials). Die Werte kommen aus der
                  App-Registrierung in Microsoft Entra. Host <code>outlook.office365.com</code>,
                  Port 993.
                </div>
                <F label="Verzeichnis-ID (Mandant)">
                  <input value={form.ms_tenant_id} onChange={(e) => setForm({ ...form, ms_tenant_id: e.target.value })} className={inputCls} placeholder="00000000-0000-0000-0000-000000000000" />
                </F>
                <F label="Anwendungs-ID (Client)">
                  <input value={form.ms_client_id} onChange={(e) => setForm({ ...form, ms_client_id: e.target.value })} className={inputCls} placeholder="00000000-0000-0000-0000-000000000000" />
                </F>
                <F label="Client Secret" hint={msSecretMasked ? "Aktuell gesetzt. Nur bei Änderung neu eingeben — Secrets laufen ab!" : "Noch nicht gesetzt."}>
                  <input type="password" placeholder={msSecretMasked ? "••••••••" : ""} value={form.ms_client_secret} onChange={(e) => setForm({ ...form, ms_client_secret: e.target.value })} className={inputCls} />
                </F>
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={async () => {
                  setMailTest({ running: true })
                  try {
                    const r = await fetch(`/api/v1/tenants/${data.slug}/test-mail-connection`, {
                      method: "POST", credentials: "include",
                    })
                    setMailTest({ res: await r.json() })
                  } catch (e) {
                    setMailTest({ res: { ok: false, imap: { ok: false, message: String(e) }, smtp: { ok: false, message: "—" } } })
                  }
                }}
                className="rounded-md border px-3 py-1.5 text-sm hover:bg-secondary"
              >
                {mailTest?.running ? "Teste…" : "Verbindung testen"}
              </button>
              <span className="text-xs text-muted-foreground">Nur Login, kein Versand. Zuerst speichern.</span>
            </div>
            {mailTest?.res && (
              <div className="space-y-1 rounded-md border p-3 text-xs">
                {(["imap", "smtp"] as const).map((k) => (
                  <div key={k} className="flex gap-2">
                    <span className={mailTest.res[k]?.ok ? "text-green-600" : "text-red-600"}>
                      {mailTest.res[k]?.ok ? "✓" : "✗"}
                    </span>
                    <span className="font-medium uppercase">{k}</span>
                    <span className="text-muted-foreground break-all">{mailTest.res[k]?.message}</span>
                  </div>
                ))}
              </div>
            )}
            <F label="Benachrichtigungs-E-Mail" hint="Erhält interne Benachrichtigungen (CC)">
              <input type="email" value={form.monteur_email} onChange={(e) => setForm({ ...form, monteur_email: e.target.value })} className={inputCls} placeholder="monteur@example.com" />
            </F>
          </Section>

          <PostfaecherSection slug={data.slug} />

          <SpamAutomatikSection slug={data.slug} />

          <ArchivZieleSection slug={data.slug} />

          <Section title="Meiti KI-Telefonie" icon="☁️" configured={!!form.meiti_ical_url}>
            <F label="Meiti iCal-URL" hint="Fuer Call-Zusammenfassungen. Kein Kalender/Buchung — das laeuft ueber den Voice-Agent.">
              <input value={form.meiti_ical_url} onChange={(e) => setForm({ ...form, meiti_ical_url: e.target.value })} className={inputCls} placeholder="https://meiti.io/..." />
            </F>
          </Section>

          <Section title="Sipgate-Rufnummern" icon="📞" configured={form.sipgate_channels.length > 0}>
            <p className="text-[12px] text-muted-foreground mb-2">
              Eingehende Anrufe an diese Rufnummern werden dem Tenant zugeordnet. Channel-Name erscheint im Anruf-Log und im Vorgang.
              Das Sipgate-Token selbst liegt global in den Environment-Variablen (gemeinsam fuer alle Kunden).
            </p>
            <div className="space-y-2">
              {form.sipgate_channels.map((c, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                  <input
                    value={c.number}
                    onChange={(e) => {
                      const next = [...form.sipgate_channels]
                      next[idx] = { ...next[idx], number: e.target.value }
                      setForm({ ...form, sipgate_channels: next })
                    }}
                    className={inputCls}
                    placeholder="+4972198618918"
                  />
                  <input
                    value={c.channel_name}
                    onChange={(e) => {
                      const next = [...form.sipgate_channels]
                      next[idx] = { ...next[idx], channel_name: e.target.value }
                      setForm({ ...form, sipgate_channels: next })
                    }}
                    className={inputCls}
                    placeholder="z.B. Kanaltec"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, sipgate_channels: form.sipgate_channels.filter((_, i) => i !== idx) })}
                    className="px-2 py-1.5 text-[12px] rounded border hover:bg-muted"
                  >
                    Entfernen
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, sipgate_channels: [...form.sipgate_channels, { number: "", channel_name: "" }] })}
              className="mt-2 px-3 py-1.5 text-[12px] rounded border hover:bg-muted"
            >
              + Rufnummer
            </button>
          </Section>

          <Section title="Buerozeiten (Sipgate-Ticket)" icon="⏰" configured={!!(form.office_hours.start && form.office_hours.end && form.office_hours.days.length)}>
            <p className="text-[12px] text-muted-foreground mb-2">
              Vorgaenge fuer Sipgate-Anrufe werden nur innerhalb dieser Zeiten erstellt. Leere Felder = keine Vorgaenge.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <F label="Start">
                <input type="time" value={form.office_hours.start}
                  onChange={(e) => setForm({ ...form, office_hours: { ...form.office_hours, start: e.target.value } })}
                  className={inputCls} />
              </F>
              <F label="Ende">
                <input type="time" value={form.office_hours.end}
                  onChange={(e) => setForm({ ...form, office_hours: { ...form.office_hours, end: e.target.value } })}
                  className={inputCls} />
              </F>
            </div>
            <F label="Wochentage">
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => {
                  const active = form.office_hours.days.includes(d.id)
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => {
                        const days = active
                          ? form.office_hours.days.filter((x) => x !== d.id)
                          : [...form.office_hours.days, d.id].sort((a, b) => a - b)
                        setForm({ ...form, office_hours: { ...form.office_hours, days } })
                      }}
                      className={cn(
                        "px-3 py-1 text-[12px] rounded border",
                        active ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"
                      )}
                    >
                      {d.label}
                    </button>
                  )
                })}
              </div>
            </F>
          </Section>

          <Section title="Team-Rufnummern (Inhaber, Mitarbeiter)" icon="👤" configured={!!form.telefon_namen.trim()}>
            <p className="text-[12px] text-muted-foreground mb-2">
              Eine Person pro Zeile. Gilt für:
              <br />• <strong>Anrufe:</strong> wer bei sipgate abgenommen hat, erscheint mit Namen statt Nummer.
              <br />• <strong>WhatsApp:</strong> Nachrichten von diesen Nummern sind „Intern“ (nicht Kunde), lassen sich
              über „Inhaber anschreiben“ von sich aus anschreiben, und aus ihnen kommen Aufgaben-Vorschläge
              (wenn unter „Nachrichten“ eingeschaltet). Handynummern also mit eintragen.
              <br />• <strong>✉️ Mails:</strong> nur wer hier den Haken hat, darf über den WhatsApp-Assistenten
              E-Mails an Kunden schicken (aus dem Postfach des Betriebs). Standard: niemand.
            </p>
            <TeamRufnummern wert={form.telefon_namen} inputCls={inputCls}
              onChange={(text) => setForm((f) => ({ ...f, telefon_namen: text }))}
              mailNummern={form.wa_mail_nummern}
              onMailNummern={(l) => setForm((f) => ({ ...f, wa_mail_nummern: l }))} />
          </Section>

          <Section title="Booking-Config (Terminvergabe)" icon="🕐" configured={form.booking.business_hour_start > 0 || form.booking.business_hour_end > 0}>
            <div className="grid grid-cols-2 gap-3">
              <F label="Oeffnungszeit Start (Stunde)" hint="0-23, z.B. 7 = 07:00">
                <input type="number" min={0} max={23} value={form.booking.business_hour_start}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, business_hour_start: parseInt(e.target.value) || 0 } })}
                  className={inputCls} />
              </F>
              <F label="Oeffnungszeit Ende (Stunde)" hint="1-24, z.B. 18 = 18:00">
                <input type="number" min={1} max={24} value={form.booking.business_hour_end}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, business_hour_end: parseInt(e.target.value) || 18 } })}
                  className={inputCls} />
              </F>
            </div>
            <F label="Abweichende Zeiten pro Tag (optional)" hint="Leer = Standard oben. Z.B. Freitag 8–14 Uhr.">
              <div className="flex flex-col gap-1.5">
                {WEEKDAYS_BOOKING.filter((d) => form.booking.working_days.includes(d.id)).map((d) => {
                  const key = String(d.id)
                  const ov = form.booking.business_hours_by_day?.[key]
                  const setOv = (patch: Partial<{ start: number; end: number }> | null) => {
                    const next = { ...(form.booking.business_hours_by_day || {}) }
                    if (patch === null) {
                      delete next[key]
                    } else {
                      const base = ov || { start: form.booking.business_hour_start, end: form.booking.business_hour_end }
                      next[key] = { ...base, ...patch }
                    }
                    setForm({ ...form, booking: { ...form.booking, business_hours_by_day: next } })
                  }
                  return (
                    <div key={d.id} className="flex items-center gap-2 text-[13px]">
                      <span className="w-8 text-muted-foreground">{d.label}</span>
                      {ov ? (
                        <>
                          <input type="number" min={0} max={23} value={ov.start}
                            onChange={(e) => setOv({ start: parseInt(e.target.value) || 0 })}
                            className={inputCls} style={{ maxWidth: 70 }} />
                          <span className="text-muted-foreground">–</span>
                          <input type="number" min={1} max={24} value={ov.end}
                            onChange={(e) => setOv({ end: parseInt(e.target.value) || 0 })}
                            className={inputCls} style={{ maxWidth: 70 }} />
                          <span className="text-muted-foreground text-[12px]">Uhr</span>
                          <button type="button" onClick={() => setOv(null)}
                            className="text-[12px] text-red-500 hover:underline ml-1">zurücksetzen</button>
                        </>
                      ) : (
                        <button type="button" onClick={() => setOv({})}
                          className="text-[12px] text-primary hover:underline">abweichende Zeit setzen</button>
                      )}
                    </div>
                  )
                })}
              </div>
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Standard-Termindauer (Min)" hint="60, 90, 120, 180">
                <input type="number" min={15} step={15} value={form.booking.default_slot_duration_min}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, default_slot_duration_min: parseInt(e.target.value) || 120 } })}
                  className={inputCls} />
              </F>
              <F label="Vorlaufzeit (Stunden)" hint="Kein Termin innerhalb dieser Zeit">
                <input type="number" min={0} max={168} value={form.booking.lead_time_hours}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, lead_time_hours: parseInt(e.target.value) || 0 } })}
                  className={inputCls} />
              </F>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F
                label="Termin nennen als"
                hint="So wird der Termin dem Kunden genannt — vom Telefon-Bot, in SMS, auf der Bestätigungsseite und in Mails. „Automatisch“ = wie bisher. Der Kalendereintrag in HERO behält immer die genaue Startzeit."
              >
                <select value={form.booking.termin_formulierung ?? ''}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, termin_formulierung: e.target.value as BookingConfig['termin_formulierung'] } })}
                  className={inputCls} aria-label="Termin nennen als">
                  <option value="">Automatisch wie bisher (mit Fenster „zwischen“, sonst „um“)</option>
                  <option value="zwischen">„zwischen 8 und 9 Uhr“</option>
                  <option value="ab">„ab 8 Uhr“</option>
                  <option value="um">„um 08:00 Uhr“ (exakte Uhrzeit)</option>
                </select>
              </F>
              <F
                label="Ankunftsfenster (Minuten)"
                hint="Für „zwischen“: 60 = „zwischen 8 und 9 Uhr“. Leer/0 bei „zwischen“ = 60 Minuten. Bei „ab“ und „um“ ohne Bedeutung."
              >
                <input type="number" min={0} max={240} step={15} value={form.booking.ankunftsfenster_min}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, ankunftsfenster_min: Math.max(0, Math.min(240, parseInt(e.target.value) || 0)) } })}
                  className={inputCls} style={{ maxWidth: 90 }} aria-label="Ankunftsfenster (Minuten)" />
              </F>
            </div>
            <F
              label="Kategorien, die keine Zeit blockieren"
              hint="Namen der HERO-Kalenderkategorien, kommagetrennt. Termine dieser Kategorien (z. B. „Information“ für Aushänge oder Notizen) zählen in der Terminsuche nicht als belegt — für Telefonbot, Portal und E-Mail-Planer. Die Kategorie selbst wird in HERO angelegt."
            >
              <input type="text" value={form.booking.info_kategorien.join(", ")}
                onChange={(e) => setForm({ ...form, booking: { ...form.booking,
                  info_kategorien: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) } })}
                className={inputCls} placeholder="Information" />
            </F>
            <F
              label="Abwesenheits-Kategorien"
              hint="HERO-Kalenderkategorien, die bedeuten, dass ein Mitarbeiter nicht da ist (Urlaub, krank). Wünscht ein Anrufer einen Rückruf von dieser Person, steht die Abwesenheit als Hinweis in der Rückruf-Aufgabe — der Bot sagt sie dem Anrufer nicht."
            >
              <input type="text" value={form.booking.abwesenheit_kategorien.join(", ")}
                onChange={(e) => setForm({ ...form, booking: { ...form.booking,
                  abwesenheit_kategorien: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) } })}
                className={inputCls} placeholder="Urlaub, Abwesenheit, Krank, Frei" />
            </F>
            <F
              label="Einzugsgebiet (Postleitzahlen)"
              hint="Postleitzahlen oder PLZ-Anfänge, kommagetrennt: „76“ deckt alle PLZ mit 76…, „76327“ genau Pfinztal. Liegt die Einsatzadresse eines Anrufers außerhalb, bietet der Telefon-Bot keinen Termin an, sondern sagt, dass das Büro prüft, ob die Adresse im Einzugsgebiet liegt, und nimmt einen Rückruf auf. Leer = keine Prüfung."
            >
              <input type="text" value={form.booking.einzugsgebiet_plz.join(", ")}
                onChange={(e) => setForm({ ...form, booking: { ...form.booking,
                  einzugsgebiet_plz: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) } })}
                className={inputCls} placeholder="76, 75, 68" />
            </F>
            <F label="Mittagspause">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={form.booking.lunch_break_enabled}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, lunch_break_enabled: e.target.checked } })} />
                Aktiv
              </label>
            </F>
            {form.booking.lunch_break_enabled && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Mittag Start">
                  <input type="number" min={0} max={23} value={form.booking.lunch_break_start}
                    onChange={(e) => setForm({ ...form, booking: { ...form.booking, lunch_break_start: parseInt(e.target.value) || 12 } })}
                    className={inputCls} />
                </F>
                <F label="Mittag Ende">
                  <input type="number" min={1} max={24} value={form.booking.lunch_break_end}
                    onChange={(e) => setForm({ ...form, booking: { ...form.booking, lunch_break_end: parseInt(e.target.value) || 13 } })}
                    className={inputCls} />
                </F>
              </div>
            )}
            <F
              label="Max. Termine pro Tag"
              hint="Wie viele Termine an einem Tag höchstens stattfinden dürfen — über den ganzen Betrieb gezählt, nicht pro Monteur. Ist das Kontingent voll, wird der Tag gar nicht mehr vorgeschlagen und auch eine gezielte Wunschzeit abgelehnt. 0 = unbegrenzt. Urlaub und mehrtägige Blocker zählen nicht mit."
            >
              <input type="number" min={0} max={100} value={form.booking.max_appointments_per_day}
                onChange={(e) => setForm({ ...form, booking: { ...form.booking, max_appointments_per_day: Math.max(0, parseInt(e.target.value) || 0) } })}
                className={inputCls} style={{ maxWidth: 90 }} />
            </F>
            <F
              label="Ganztages-Einschub"
              hint="Wenn ein Monteur einen mehrtägigen Ganztages-Termin hat (z.B. Großbaustelle Mo–Mi), darf man trotzdem feste Stunden einschieben (Standard 8 + 15 Uhr, je 1×). Einzelner Ganztag bleibt komplett gesperrt."
            >
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                <input type="checkbox" checked={form.booking.allday_squeeze_enabled}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, allday_squeeze_enabled: e.target.checked } })} />
                Aktiv
              </label>
            </F>
            {form.booking.allday_squeeze_enabled && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Ab wie vielen Tagen" hint="Erst ab einem Ganztages-Block dieser Länge greift der Einschub (min. 2).">
                  <input type="number" min={2} max={30} value={form.booking.allday_squeeze_min_days}
                    onChange={(e) => setForm({ ...form, booking: { ...form.booking, allday_squeeze_min_days: Math.max(2, parseInt(e.target.value) || 2) } })}
                    className={inputCls} style={{ maxWidth: 90 }} />
                </F>
                <F label="Einschub-Stunden" hint="Komma-getrennte Start-Stunden, z.B. 8, 15">
                  <input type="text"
                    value={form.booking.allday_squeeze_hours.join(", ")}
                    onChange={(e) => {
                      const hours = e.target.value.split(",")
                        .map((x) => parseInt(x.trim()))
                        .filter((x) => !isNaN(x) && x >= 0 && x <= 23)
                      setForm({ ...form, booking: { ...form.booking, allday_squeeze_hours: hours } })
                    }}
                    className={inputCls} style={{ maxWidth: 150 }} />
                </F>
              </div>
            )}
            <F
              label="Buchungssperre"
              hint="Zeiträume, in denen NICHT automatisch gebucht wird (Betriebsurlaub, Feiertag, Messe). Gilt für alle automatischen Kanäle: Telefon-Bot, E-Mail-Planer, Sipgate und Chatbot. Beide Tage sind komplett gesperrt — „24.12. bis 02.01.“ blockt den 02.01. mit. Wiederkehrendes (Wochentage, Randzeiten, Mittagspause) gehört in die Arbeitszeiten oben, nicht hierher."
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {form.booking.blackout_periods.map((p, i) => {
                  const invalid = !!p.start && !!p.end && p.end < p.start
                  const patch = (field: keyof BlackoutPeriod, val: string) =>
                    setForm({
                      ...form,
                      booking: {
                        ...form.booking,
                        blackout_periods: form.booking.blackout_periods.map(
                          (x, j) => (j === i ? { ...x, [field]: val } : x),
                        ),
                      },
                    })
                  return (
                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <input type="date" value={p.start} onChange={(e) => patch("start", e.target.value)}
                          className={inputCls} style={{ maxWidth: 155 }} />
                        <span style={{ fontSize: 13, opacity: 0.7 }}>bis</span>
                        <input type="date" value={p.end} onChange={(e) => patch("end", e.target.value)}
                          className={inputCls} style={{ maxWidth: 155 }} />
                        <input type="text" placeholder="Grund, z.B. Betriebsurlaub" value={p.reason}
                          onChange={(e) => patch("reason", e.target.value)}
                          className={inputCls} style={{ flex: 1, minWidth: 170 }} />
                        <button type="button" title="Zeitraum entfernen"
                          onClick={() => setForm({
                            ...form,
                            booking: {
                              ...form.booking,
                              blackout_periods: form.booking.blackout_periods.filter((_, j) => j !== i),
                            },
                          })}
                          style={{ padding: "4px 9px", fontSize: 15, lineHeight: 1, cursor: "pointer" }}>×</button>
                      </div>
                      {invalid && (
                        <span style={{ fontSize: 12, color: "#dc2626" }}>
                          Ende liegt vor dem Start — dieser Zeitraum wird ignoriert.
                        </span>
                      )}
                    </div>
                  )
                })}
                <button type="button"
                  onClick={() => setForm({
                    ...form,
                    booking: {
                      ...form.booking,
                      blackout_periods: [...form.booking.blackout_periods, { start: "", end: "", reason: "" }],
                    },
                  })}
                  style={{ alignSelf: "flex-start", padding: "5px 11px", fontSize: 13, cursor: "pointer" }}>
                  + Zeitraum hinzufügen
                </button>
                {form.booking.blackout_periods.length === 0 && (
                  <span style={{ fontSize: 12, opacity: 0.6 }}>Keine Sperre aktiv — es wird jederzeit gebucht.</span>
                )}
              </div>
            </F>
            <F
              label="Wiederkehrende Sperrzeiten"
              hint="Feste Zeitfenster jede Woche, in denen NICHT automatisch gebucht wird — z. B. dienstags 8–12 Uhr Bürotag. Gilt wie die Buchungssperre für Telefon-Bot, E-Mail-Planer und Chatbot."
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {form.booking.sperrzeiten_woechentlich.map((w, i) => {
                  const patch = (neu: Partial<WochenSperre>) =>
                    setForm({
                      ...form,
                      booking: {
                        ...form.booking,
                        sperrzeiten_woechentlich: form.booking.sperrzeiten_woechentlich.map(
                          (x, j) => (j === i ? { ...x, ...neu } : x),
                        ),
                      },
                    })
                  return (
                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        <select value={w.tag} onChange={(e) => patch({ tag: parseInt(e.target.value) })}
                          className={inputCls} style={{ maxWidth: 90 }}>
                          {WEEKDAYS_BOOKING.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                        </select>
                        <input type="number" min={0} max={23} value={w.von}
                          onChange={(e) => patch({ von: parseInt(e.target.value) || 0 })}
                          className={inputCls} style={{ maxWidth: 70 }} />
                        <span style={{ fontSize: 13, opacity: 0.7 }}>bis</span>
                        <input type="number" min={1} max={24} value={w.bis}
                          onChange={(e) => patch({ bis: parseInt(e.target.value) || 0 })}
                          className={inputCls} style={{ maxWidth: 70 }} />
                        <span style={{ fontSize: 13, opacity: 0.7 }}>Uhr</span>
                        <input type="text" placeholder="Grund, z.B. Bürotag" value={w.grund}
                          onChange={(e) => patch({ grund: e.target.value })}
                          className={inputCls} style={{ flex: 1, minWidth: 150 }} />
                        <button type="button" title="Sperrzeit entfernen"
                          onClick={() => setForm({
                            ...form,
                            booking: {
                              ...form.booking,
                              sperrzeiten_woechentlich: form.booking.sperrzeiten_woechentlich.filter((_, j) => j !== i),
                            },
                          })}
                          style={{ padding: "4px 9px", fontSize: 15, lineHeight: 1, cursor: "pointer" }}>×</button>
                      </div>
                      {!(w.von < w.bis) && (
                        <span style={{ fontSize: 12, color: "#dc2626" }}>
                          „bis“ muss nach „von“ liegen — diese Sperrzeit wird ignoriert.
                        </span>
                      )}
                    </div>
                  )
                })}
                <button type="button"
                  onClick={() => setForm({
                    ...form,
                    booking: {
                      ...form.booking,
                      sperrzeiten_woechentlich: [
                        ...form.booking.sperrzeiten_woechentlich, { tag: 1, von: 8, bis: 12, grund: "" },
                      ],
                    },
                  })}
                  style={{ alignSelf: "flex-start", padding: "5px 11px", fontSize: 13, cursor: "pointer" }}>
                  + Sperrzeit hinzufügen
                </button>
              </div>
            </F>
            <F
              label="Besichtigungen"
              hint="Gilt nur für Besichtigungen (erkannt an den Stichworten in Anliegen oder Termintitel), nicht für Wartung, Reparatur oder Umsetzung. Zuerst werden nur die Besichtigungstage ab der Uhrzeit angeboten; ist dort in den nächsten Tagen nichts frei, auch andere Tage — dann höchstens so viele Besichtigungen pro Tag wie angegeben."
            >
              {(() => {
                const r = form.booking.besichtigung_regel
                const setR = (neu: Partial<BesichtigungRegel>) =>
                  setForm({ ...form, booking: { ...form.booking, besichtigung_regel: { ...r, ...neu } } })
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={r.aktiv} onChange={(e) => setR({ aktiv: e.target.checked })} />
                      <span>Besichtigungs-Regel aktiv</span>
                    </label>
                    {r.aktiv && (
                      <>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
                          <span>Besichtigungstage:</span>
                          {WEEKDAYS_BOOKING.map((d) => (
                            <label key={d.id} style={{ display: "inline-flex", alignItems: "center", gap: 3, cursor: "pointer" }}>
                              <input type="checkbox" checked={r.tage.includes(d.id)}
                                onChange={(e) => setR({
                                  tage: e.target.checked
                                    ? [...r.tage, d.id].sort((a, b) => a - b)
                                    : r.tage.filter((t) => t !== d.id),
                                })} />
                              {d.label}
                            </label>
                          ))}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <F label="ab Uhrzeit" hint="An den Besichtigungstagen frühestens um">
                            <input type="number" min={0} max={23} value={r.ab_stunde}
                              onChange={(e) => setR({ ab_stunde: parseInt(e.target.value) || 0 })}
                              className={inputCls} />
                          </F>
                          <F label="Vorrang (Tage)" hint="So viele Tage voraus zuerst nur Besichtigungstage anbieten">
                            <input type="number" min={1} max={60} value={r.bevorzugt_tage_voraus}
                              onChange={(e) => setR({ bevorzugt_tage_voraus: parseInt(e.target.value) || 7 })}
                              className={inputCls} />
                          </F>
                          <F label="Max. pro Tag sonst" hint="An anderen Tagen höchstens so viele Besichtigungen">
                            <input type="number" min={0} max={20} value={r.max_pro_tag_sonst}
                              onChange={(e) => setR({ max_pro_tag_sonst: Math.max(0, parseInt(e.target.value) || 0) })}
                              className={inputCls} />
                          </F>
                        </div>
                        <F label="Stichworte" hint="Komma-getrennt; kommt eines davon im Anliegen oder Termintitel vor, gilt der Termin als Besichtigung">
                          {/* Beim Verlassen uebernehmen — bei jedem Tastendruck
                              zerlegt, liesse sich nach einem Komma kein neues
                              Wort mehr anfangen. */}
                          <input type="text" key={r.stichworte.join(",")} defaultValue={r.stichworte.join(", ")}
                            onBlur={(e) => setR({
                              stichworte: e.target.value.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
                            })}
                            className={inputCls} />
                        </F>
                      </>
                    )}
                  </div>
                )
              })()}
            </F>
            <F
              label="Wartung"
              hint="Wartungsanfragen bekommen direkt einen Wartungstermin, nie eine Besichtigung vorab. Telefonbot und Mail-Vorschlag fragen nach der Typennummer vom Typenschild (weiß der Kunde sie nicht: Foto erbitten, Termin trotzdem). Die Typennummer steht dann in der Terminbeschreibung und im HERO-Logbuch. Anfragen mit neuer Anlage, Austausch oder Umbau gelten nicht als Wartung."
            >
              {(() => {
                const w = form.booking.wartung_regel ?? DEFAULT_BOOKING.wartung_regel
                const setW = (neu: Partial<WartungRegel>) =>
                  setForm({ ...form, booking: { ...form.booking, wartung_regel: { ...w, ...neu } } })
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                      <input type="checkbox" checked={w.aktiv} onChange={(e) => setW({ aktiv: e.target.checked })} />
                      <span>Wartung direkt buchen + Typennummer erfragen</span>
                    </label>
                    {w.aktiv && (
                      <F label="Stichworte" hint="Komma-getrennt; kommt eines davon in Anfrage, Anliegen oder Titel vor, gilt sie als Wartung">
                        <input type="text" key={w.stichworte.join(",")} defaultValue={w.stichworte.join(", ")}
                          onBlur={(e) => setW({
                            stichworte: e.target.value.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
                          })}
                          className={inputCls} />
                      </F>
                    )}
                  </div>
                )
              })()}
            </F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Such-Tage" hint="Wie viele Tage in die Zukunft nach Slots suchen">
                <input type="number" min={1} max={90} value={form.booking.search_days}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, search_days: parseInt(e.target.value) || 14 } })}
                  className={inputCls} />
              </F>
              <F label="Max. Alternativen" hint="Wie viele Slot-Vorschläge pro Anfrage">
                <input type="number" min={1} max={10} value={form.booking.max_alt}
                  onChange={(e) => setForm({ ...form, booking: { ...form.booking, max_alt: parseInt(e.target.value) || 3 } })}
                  className={inputCls} />
              </F>
            </div>
            <F label="Raster (Minuten)" hint="Schrittweite bei Alternativsuche (z.B. 60 = stündlich, 30 = halbstündlich)">
              <input type="number" min={5} max={240} step={5} value={form.booking.slot_step_min}
                onChange={(e) => setForm({ ...form, booking: { ...form.booking, slot_step_min: parseInt(e.target.value) || 60 } })}
                className={inputCls} style={{ maxWidth: 150 }} />
            </F>
            <F label="Arbeitstage" hint="An welchen Tagen werden Termine vorgeschlagen">
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS_BOOKING.map((d) => {
                  const active = form.booking.working_days.includes(d.id)
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => {
                        const days = active
                          ? form.booking.working_days.filter((x) => x !== d.id)
                          : [...form.booking.working_days, d.id].sort((a, b) => a - b)
                        setForm({ ...form, booking: { ...form.booking, working_days: days } })
                      }}
                      className={cn(
                        "px-3 py-1 text-[12px] rounded border",
                        active ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"
                      )}
                    >
                      {d.label}
                    </button>
                  )
                })}
              </div>
            </F>
            <F
              label="Monteure für Terminfindung"
              hint="Welche Monteure werden bei der Slot-Suche berücksichtigt? Slot ist frei, sobald MIND. EINER der ausgewählten Monteure verfügbar ist (Urlaub von Monteur A blockt nicht den Slot von Monteur B)."
            >
              <div className="flex flex-col gap-1.5">
                {/* Ohne Auswahl kann gar nicht gebucht werden — das muss man
                    sehen, bevor ein Anrufer einen Termin zugesagt bekommt, den
                    es nicht gibt (Testanruf 26.08.: Slot war frei, hier war
                    nichts angehakt, die Buchung scheiterte danach mit 409). */}
                {form.booking.relevant_partner_ids.length === 0 &&
                  !form.booking.main_partner_id && (
                    <div className="rounded border border-amber-500/50 bg-amber-500/10 px-2.5 py-2 text-[12px] text-amber-700 dark:text-amber-400">
                      <strong>Kein Monteur ausgewählt.</strong> Solange hier nichts
                      angehakt ist, kann für diesen Mandanten kein Termin
                      automatisch gebucht werden — der Telefon-Assistent bietet
                      dann gar keine Termine mehr an und nimmt stattdessen einen
                      Rückruf auf.
                    </div>
                  )}
                {(monteureData?.items || []).filter((m) => m.hero_partner_id).length === 0 ? (
                  <div className="text-[12px] text-muted-foreground italic">
                    Keine Monteure mit HERO-Verknüpfung vorhanden. In der Monteur-Verwaltung anlegen.
                  </div>
                ) : (
                  (monteureData?.items || []).filter((m) => m.hero_partner_id).map((m) => {
                    const pid = m.hero_partner_id as number
                    const checked = form.booking.relevant_partner_ids.includes(pid)
                    return (
                      <label key={m.id} className="flex items-center gap-2 text-[13px] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? form.booking.relevant_partner_ids.filter((x) => x !== pid)
                              : [...form.booking.relevant_partner_ids, pid].sort((a, b) => a - b)
                            setForm({ ...form, booking: { ...form.booking, relevant_partner_ids: next } })
                          }}
                        />
                        <span>{m.name}</span>
                        <span className="text-[11px] text-muted-foreground">#{pid}</span>
                      </label>
                    )
                  })
                )}
              </div>
            </F>
            <F
              label="Standard-Monteur fuer auto-gebuchte Termine"
              hint="Wird zugewiesen wenn Review/Voice/Sipgate/etc. einen Termin automatisch anlegen. Leer = zufaellig einer aus den oben ausgewaehlten Monteuren."
            >
              <select
                value={form.booking.main_partner_id ?? ""}
                onChange={(e) => {
                  const v = e.target.value
                  setForm({ ...form, booking: { ...form.booking, main_partner_id: v ? Number(v) : null } })
                }}
                className={inputCls}
              >
                <option value="">— zufaellig waehlen —</option>
                {(monteureData?.items || [])
                  .filter((m) => m.hero_partner_id)
                  .map((m) => (
                    <option key={m.id} value={m.hero_partner_id as number}>
                      {m.name} (#{m.hero_partner_id})
                    </option>
                  ))}
              </select>
            </F>
            <F
              label="HERO Kalender-Kategorie"
              hint="Kategorie, die neue Termine bekommen — auch alle automatisch gebuchten. PFLICHT: ohne sie bricht HERO mit 'Record not found in calendar_event_categories' ab."
            >
              {(() => {
                const kats = kategorienData?.categories || []
                const wert = form.hero_calendar_category_id
                const unbekannt = wert != null && kats.length > 0 && !kats.some((k) => Number(k.id) === Number(wert))
                return (
                  <>
                    <select
                      value={wert ?? ""}
                      onChange={(e) => {
                        const v = e.target.value
                        setForm({ ...form, hero_calendar_category_id: v ? Number(v) : null })
                      }}
                      className={inputCls}
                    >
                      <option value="">— keine —</option>
                      {/* Ein gespeicherter Wert, den HERO nicht kennt, braucht eine
                          eigene Option: sonst zeigt das Feld stumm die erste
                          Kategorie und speichert sie beim naechsten Klick mit. */}
                      {unbekannt && <option value={String(wert)}>⚠ unbekannte ID #{wert}</option>}
                      {kats.map((k) => (
                        <option key={k.id} value={k.id}>{k.name} (#{k.id})</option>
                      ))}
                    </select>
                    {unbekannt && (
                      <div className="text-xs text-destructive mt-1">
                        HERO kennt die ID #{String(wert)} nicht — jede automatische Buchung scheitert damit.
                      </div>
                    )}
                    {kats.length === 0 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Kategorien nicht abrufbar (HERO-Token?) — Wert bleibt unveraendert.
                      </div>
                    )}
                  </>
                )
              })()}
            </F>
            <F
              label="Aufgaben-Ersteller (Default)"
              hint="Wer als Ersteller in HERO eingetragen wird, wenn das Portal eine Aufgabe anlegt (Review-Plan, Mail-zu-Aufgabe, Sipgate-Calls etc.). Typischerweise der Buero-Account."
            >
              <select
                value={form.hero_default_author_user_id ?? ""}
                onChange={(e) => {
                  const v = e.target.value
                  setForm({ ...form, hero_default_author_user_id: v ? Number(v) : null })
                }}
                className={inputCls}
              >
                <option value="">— kein Default (HERO setzt selbst) —</option>
                {(monteureData?.items || [])
                  .filter((m) => m.hero_user_id)
                  .map((m) => (
                    <option key={m.id} value={m.hero_user_id as number}>
                      {m.name} (user_id #{m.hero_user_id})
                    </option>
                  ))}
              </select>
            </F>
          </Section>

          <Section title="Nachrichten (SMS / WhatsApp)" icon="📱"
            configured={Object.values(KANAL_FELDER).some((f) => form[f] !== "aus")}>
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
              Je Nachricht: <strong>Aus</strong>, <strong>SMS</strong> oder <strong>WhatsApp</strong> (kommt sie per
              WhatsApp nicht an, geht automatisch die SMS raus; ist WhatsApp noch nicht eingerichtet, wird SMS gesendet).
            </div>

            <F label="Kunde: Daten-Abgleich nach dem Anruf" hint="Nach einer Terminbuchung bekommt der Kunde einen Link, auf dem er seine Daten prüft. Aus = es geht nichts raus.">
              <KanalWahl wert={form.kanal_kunde_abgleich} testid="kanal-kunde-abgleich"
                onChange={(k) => setForm({ ...form, kanal_kunde_abgleich: k })} />
            </F>

            <F label="Nur an Neukunden" hint="Bestandskunden haben in HERO bereits gepruefte Daten — dort bleibt vor allem die Terminbestaetigung als Nutzen. Ohne Haken bekommen alle die Nachricht.">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.sms_nur_neukunden}
                  onChange={(e) => setForm({ ...form, sms_nur_neukunden: e.target.checked })}
                  disabled={form.kanal_kunde_abgleich === "aus"}
                />
                <span>Nur bei neu angelegten Kontakten</span>
              </label>
            </F>

            <F label="Auch ohne Terminbuchung" hint="Ohne Haken gibt es den Daten-Abgleich nur nach einer Terminbuchung. Mit Haken bekommt ihn auch, wer ohne Termin auflegt und dessen Name im Gespraech aufgenommen wurde (z. B. Rueckrufwunsch ausserhalb der Buerozeiten).">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.sms_ohne_buchung}
                  onChange={(e) => setForm({ ...form, sms_ohne_buchung: e.target.checked })}
                  disabled={form.kanal_kunde_abgleich === "aus"}
                />
                <span>Daten-Abgleich auch bei Anrufen ohne Buchung</span>
              </label>
            </F>

            <F label="Terminlink (Kunde wählt Termin selbst)" hint="Am Vorgang „Terminvorschläge senden“: drei freie Termine werden als BLOCKER in HERO reserviert, der Kunde bekommt einen Link (SMS/WhatsApp, E-Mail oder zum Kopieren) und wählt einen davon oder nennt eigene Zeiten. Das Büro bestätigt unter Kunden-Rückmeldungen, die Bestätigung geht automatisch raus. Ohne Reaktion nach 48 Stunden werden die Blocker entfernt und der Kunde steht zum Anrufen auf der Liste.">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.terminlink_aktiv}
                  onChange={(e) => setForm({ ...form, terminlink_aktiv: e.target.checked })}
                  data-testid="terminlink-aktiv"
                />
                <span>Terminvorschläge per Link anbieten</span>
              </label>
            </F>

            <F label="Kunde: Terminvorschläge, Bestätigung und Absage" hint="Der Kanal für Terminlink, Terminbestätigung und „Termin klappt nicht“. Aus = der Terminlink geht nur per E-Mail oder zum Kopieren.">
              <KanalWahl wert={form.kanal_kunde_terminlink} testid="kanal-kunde-terminlink"
                onChange={(k) => setForm({ ...form, kanal_kunde_terminlink: k })} />
            </F>

            <F label="Inhaber: Meldung des Telefonassistenten" hint="Was der Bot außerhalb der Bürozeiten aufgenommen hat, geht nach dem Anruf an die im Telefonassistenten hinterlegte Nummer — bei „E-Mail“ mit allen erfassten Daten an die E-Mail-Adresse des Betriebs. Aus = keine Meldung, alles steht nur im Portal.">
              <KanalWahl wert={form.kanal_betrieb_meldung} testid="kanal-betrieb-meldung" mitEmail
                onChange={(k) => setForm({ ...form, kanal_betrieb_meldung: k })} />
            </F>

            <F label="Inhaber: Notfall-Meldung vor dem Durchstellen" hint="Bei einem Notfall geht die Meldung raus, BEVOR durchgestellt wird — damit sie auch dann vorliegt, wenn niemand abnimmt. Vorsicht mit „Aus“.">
              <KanalWahl wert={form.kanal_betrieb_notfall} testid="kanal-betrieb-notfall"
                onChange={(k) => setForm({ ...form, kanal_betrieb_notfall: k })} />
            </F>

            <F label="Aufgaben-Vorschläge aus Inhaber-WhatsApps" hint="Schreibt der Inhaber dem Büro per WhatsApp, schlägt die KI im WhatsApp-Posteingang eine Aufgabe vor — angelegt wird sie erst per Klick (HERO + Portal). Die Nummer des Inhabers muss unter „Team-Rufnummern“ stehen.">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.wa_aufgaben_vorschlaege}
                  onChange={(e) => setForm({ ...form, wa_aufgaben_vorschlaege: e.target.checked })}
                  data-testid="wa-aufgaben-vorschlaege"
                />
                <span>Aufgaben vorschlagen (anlegen nur per Klick)</span>
              </label>
            </F>

            <F label="WhatsApp-Assistent für den Inhaber" hint="Der Inhaber schreibt oder spricht per WhatsApp: (1) Auftrag beschreiben und Angebot wünschen → Entwurf unter „KI-Angebote“, Antwort mit Zusammenfassung und Link; Antworten wie „nimm die PU-Wanne“ ändern den Entwurf. (2) „Was steht an?“ → offene Aufgaben nach Dringlichkeit. (3) Arbeitsbericht diktieren → Zusammenfassung mit zugeordnetem Termin; erst nach „JA“ entsteht ein Entwurf im HERO-Projekt. Nach HERO geht nie etwas von allein. Die Nummer des Inhabers muss unter „Team-Rufnummern“ stehen.">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.wa_angebote}
                  onChange={(e) => setForm({ ...form, wa_angebote: e.target.checked })}
                  data-testid="wa-angebote"
                />
                <span>Angebote, Aufgaben-Abfrage und Arbeitsberichte per WhatsApp</span>
              </label>
            </F>

            <F label="Fragen an den Inhaber per WhatsApp" hint="Das Büro stellt im Reiter „Aufgaben“ Fragen an den Inhaber — zu einer Aufgabe oder frei. Sie gehen werktags um 08:00 per WhatsApp raus (oder sofort über „Jetzt senden“), mit bis zu drei Antwort-Knöpfen. Die Antwort steht danach in der Spalte und, wenn es einen Vorgang gibt, wörtlich im HERO-Logbuch. Es entsteht nie automatisch eine Aufgabe. Erst einschalten, wenn der Inhaber zugestimmt hat. Seine Nummer muss unter „Team-Rufnummern“ mit „(Inhaber)“ stehen.">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.inhaber_fragen}
                  onChange={(e) => setForm({ ...form, inhaber_fragen: e.target.checked })}
                  data-testid="inhaber-fragen"
                />
                <span>Fragen an den Inhaber per WhatsApp senden</span>
              </label>
            </F>

            <F label="Was darf der WhatsApp-Assistent?" hint="Je Funktion ein- oder ausschalten. Ausgeschaltetes macht der Assistent nicht — er sagt dem Inhaber dann, dass es für den Betrieb nicht freigeschaltet ist; „Hilfe“ und „Was ist neu?“ zeigen nur, was an ist. Neue Funktionen sind zunächst an.">
              <WaFunktionen
                katalog={((data as any).wa_funktionen_katalog || []) as WaFunktionGruppe[]}
                werte={form.wa_funktionen}
                deaktiviert={!form.wa_angebote}
                onChange={(w) => setForm({ ...form, wa_funktionen: w })}
              />
            </F>

            <F label="Dokumenttyp für WhatsApp-Arbeitsberichte" hint="In diesem HERO-Dokumenttyp legt der Assistent Arbeitsberichte als Entwurf an. „Standard“ = der erste aktive Typ mit „Arbeitsbericht“ im Namen. Gibt es den gewählten Typ in HERO nicht mehr, legt der Assistent nichts an und sagt es.">
              {(() => {
                const typen = dokumenttypenData?.typen || []
                const gespeichert = form.wa_bericht_dokumenttyp.trim()
                const klein = gespeichert.toLowerCase()
                // gespeichert ist eine ID oder (von Hand gesetzt) ein Name — beides zeigt den Typ an
                const treffer = typen.find((t) => String(t.id) === gespeichert || t.name.toLowerCase() === klein)
                const wert = gespeichert ? (treffer ? String(treffer.id) : gespeichert) : ""
                const unbekannt = !!gespeichert && !treffer && typen.length > 0
                return (
                  <>
                    <select
                      value={wert}
                      onChange={(e) => setForm({ ...form, wa_bericht_dokumenttyp: e.target.value })}
                      className={inputCls}
                      data-testid="wa-bericht-dokumenttyp"
                    >
                      <option value="">Standard (erster Typ „Arbeitsbericht“)</option>
                      {/* gespeicherter Wert, den HERO nicht (mehr) kennt, braucht eine eigene Option */}
                      {gespeichert && !treffer && <option value={gespeichert}>⚠ nicht in HERO: {gespeichert}</option>}
                      {typen.map((t) => (
                        <option key={t.id} value={String(t.id)}>{t.name}</option>
                      ))}
                    </select>
                    {unbekannt && (
                      <div className="text-xs text-destructive mt-1">
                        Diesen Dokumenttyp gibt es in HERO nicht (mehr) — Arbeitsberichte werden so nicht angelegt.
                      </div>
                    )}
                    {dokumenttypenFehler && (
                      <div className="text-xs opacity-60 mt-1">Dokumenttypen aus HERO nicht abrufbar.</div>
                    )}
                  </>
                )
              })()}
            </F>

            <F label="Absenderkennung" hint="Steht beim Kunden auf dem Display als Absender. MAX 11 ZEICHEN — laengere werden vom Gateway still abgeschnitten. Nur Buchstaben empfohlen; Umlaute und Leerzeichen koennen je nach Mobilfunknetz zu Zustellproblemen fuehren. Leer = erstes Wort des Firmennamens.">
              <input
                value={form.sms_absender}
                onChange={(e) => setForm({ ...form, sms_absender: e.target.value })}
                className={inputCls}
                maxLength={11}
                placeholder="z.B. Sanitec"
              />
              <div style={{ fontSize: 12, opacity: .6, marginTop: 4 }}>
                {form.sms_absender.length}/11 Zeichen
              </div>
            </F>

            <F
              label="seven.io API-Key"
              hint={
                (data as any).sms_api_key_set
                  ? "Aktuell gesetzt — SMS dieses Betriebs laufen ueber seinen eigenen seven.io-Account. Nur bei Aenderung neu eingeben. Zum Entfernen ein einzelnes - eintragen (dann greift wieder der globale Key)."
                  : "Leer = globaler Key aus der Server-Konfiguration (SEVEN_API_KEY), die SMS gehen dann auf unsere Rechnung. Eigenen Key eintragen, damit der Betrieb selbst abrechnet."
              }
            >
              <input
                type="password"
                value={form.sms_api_key}
                onChange={(e) => setForm({ ...form, sms_api_key: e.target.value })}
                className={inputCls}
                placeholder={(data as any).sms_api_key_set ? "••••••••" : "leer = globaler Key"}
                autoComplete="new-password"
              />
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    setSmsTest({ running: true })
                    try {
                      const r = await fetch(`/api/v1/tenants/${data.slug}/test-sms-connection`, {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json" },
                        // Ungespeicherten Key mitschicken, damit man vor dem
                        // Speichern testen kann.
                        body: JSON.stringify({ api_key: form.sms_api_key || null }),
                      })
                      setSmsTest({ res: await r.json() })
                    } catch (e) {
                      setSmsTest({ res: { ok: false, message: String(e) } })
                    }
                  }}
                  className="rounded-md border px-3 py-1.5 text-sm hover:bg-secondary"
                >
                  {smsTest?.running ? "Teste…" : "Verbindung testen"}
                </button>
                <span className="text-xs text-muted-foreground">
                  Fragt nur das Guthaben ab — es geht keine SMS raus, es kostet nichts.
                </span>
              </div>
              {smsTest?.res && (
                <div className="mt-2 space-y-1 rounded-md border p-3 text-xs">
                  <div className="flex gap-2">
                    <span className={smsTest.res.ok ? "text-green-600" : "text-red-600"}>
                      {smsTest.res.ok ? "✓" : "✗"}
                    </span>
                    <span className="break-all">{smsTest.res.message}</span>
                  </div>
                  {smsTest.res.quelle === "global" && (
                    <div className="text-muted-foreground">
                      Geprueft wurde der globale Key — dieser Betrieb hat keinen eigenen.
                    </div>
                  )}
                  {smsTest.res.ok && smsTest.res.provider !== "seven" && (
                    <div className="text-amber-600">
                      Achtung: Der Server steht auf SMS_PROVIDER={String(smsTest.res.provider)} —
                      der Versand wird nur geloggt, nicht ausgeliefert.
                    </div>
                  )}
                  {smsTest.res.ok && !smsTest.res.sms_aktiv && (
                    <div className="text-muted-foreground">
                      Hinweis: SMS-Versand ist fuer diesen Betrieb noch nicht aktiviert.
                    </div>
                  )}
                </div>
              )}
            </F>

            <F label="Textvorlage" hint="Leer = Standardtext. Platzhalter: {link} (PFLICHT), {firma}, {absender}, {termin}, {vorname}, {nachname}. Ohne {link} kann der Kunde nichts bestaetigen — das Speichern wird dann abgelehnt.">
              <textarea
                value={form.sms_template}
                onChange={(e) => setForm({ ...form, sms_template: e.target.value })}
                rows={5}
                className={inputCls}
                style={{ fontFamily: "monospace", fontSize: 12 }}
                placeholder={"Hallo, hier ist Lilli von {firma}. Ich würde gerne einmal Ihre Daten abgleichen: Bitte klicken Sie auf den folgenden Link und nehmen ggf. Korrekturen vor.\n{link}\nWir freuen uns auf Ihren Termin am {termin}."}
              />
              {form.sms_template.trim() && !form.sms_template.includes("{link}") && (
                <div style={{ fontSize: 12, color: "var(--pp-danger, #c0392b)", marginTop: 6 }}>
                  Die Vorlage muss {"{link}"} enthalten — sonst kann der Kunde nichts bestaetigen.
                </div>
              )}
              {form.sms_template.trim() && (
                <div style={{ fontSize: 12, opacity: .65, marginTop: 6 }}>
                  {form.sms_template.length} Zeichen ohne Platzhalter-Ersetzung ·
                  {" "}eine SMS fasst 160 Zeichen, danach je 152 pro Teil
                </div>
              )}
            </F>
          </Section>

          <Section title="E-Mail Signatur" icon="✍️" configured={!!form.signature_html || (data as any).signature_logo_set}>
            <F label="HTML-Signatur" hint="Wird automatisch an jede gesendete E-Mail angehaengt. HTML erlaubt (fett, Links). Zeilenumbruch = <br>. Platzhalter {{SIGNATURE}} im Body setzt sie an eine bestimmte Stelle (sonst am Ende).">
              <textarea
                ref={sigRef}
                value={form.signature_html}
                onChange={(e) => setForm({ ...form, signature_html: e.target.value })}
                rows={8}
                className={inputCls}
                style={{ fontFamily: "monospace", fontSize: 12 }}
                placeholder="<p>Mit freundlichen Grüßen<br/><strong>Ihr Name</strong></p>"
              />
            </F>
            <F label="Bilder in der Signatur" hint="Fuer Bilder MITTEN in der Signatur (Firmenlogo oben, Verbandslogo im Text). Hochladen, dann steht der Platzhalter {{BILD:name}} im Text — dort erscheint das Bild. Alle Bilder werden auf dieselbe Hoehe (45px) gebracht, damit sie gleich gross wirken. CID-Anhang, funktioniert auch in Outlook und Gmail.">
              <SignaturBilder
                slug={slug!}
                namen={((data as any).signature_images as SigBild[]) || []}
                onChange={() => qc.invalidateQueries({ queryKey: ["tenant", slug] })}
                einfuegen={platzhalterEinfuegen}
              />
            </F>
            <F label="Logo (PNG/JPEG/WebP, max. 2 MB)" hint="Haengt automatisch UNTEN an. Soll es woanders stehen — z.B. ganz oben — schreib {{BILD:logo}} an die gewuenschte Stelle; der Name „logo“ meint genau dieses Bild. CID-Attachment, funktioniert auch in Outlook/Gmail.">
              <SignatureLogoUploader
                slug={slug!}
                logoSet={(data as any).signature_logo_set}
                onChange={() => qc.invalidateQueries({ queryKey: ["tenant", slug] })}
              />
            </F>
            <div className="mt-3 p-3 rounded border bg-muted/30">
              <div className="text-[11px] text-muted-foreground mb-1.5 uppercase tracking-wide">Vorschau (so landet's in der Mail)</div>
              <SignaturePreview slug={slug!} html={form.signature_html} logoSet={(data as any).signature_logo_set} bilder={((data as any).signature_images as SigBild[]) || []} />
            </div>
          </Section>

          <AdvancedSection slug={data.slug} />
        </div>

        <div className="px-6 py-4 border-t bg-secondary/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (confirm(`Tenant "${data.slug}" wirklich löschen? Diese Aktion ist unwiderruflich und löscht alle zugehörigen Daten!`)) {
                  fetch(`/api/v1/tenants/${data.slug}`, { method: "DELETE", credentials: "include" })
                    .then((r) => {
                      if (r.ok || r.type === "opaqueredirect") {
                        window.location.href = "/tenants"
                      } else {
                        alert("Fehler beim Löschen")
                      }
                    })
                    .catch((e) => alert("Fehler: " + e.message))
                }
              }}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-md bg-destructive/10 text-destructive hover:bg-destructive/20"
              title="Tenant unwiderruflich löschen"
            >
              <Trash2 className="w-3.5 h-3.5" /> Tenant löschen
            </button>
            <div className="text-[12px]">
              {save.isError && <span className="text-destructive">Fehler: {save.error instanceof Error ? save.error.message : "unbekannt"}</span>}
              {saveOk && <span className="text-success font-semibold">✓ Gespeichert</span>}
            </div>
          </div>
          <button
            type="submit"
            disabled={save.isPending}
            className={cn(
              "inline-flex items-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90",
              save.isPending && "opacity-60 cursor-wait",
            )}
          >
            <Save className="w-3.5 h-3.5" />
            {save.isPending ? "Speichert..." : "Speichern"}
          </button>
        </div>
      </form>
    </div>
  )
}

// Kanal je Nachrichtenart (Paul 08.10.2026) — Werte wie services/kanal.py.
// "email" nur für die Meldung nach dem Anruf (Tischendorf 22.10.2026).
type Kanal = "aus" | "sms" | "whatsapp" | "email"
const KANAL_FELDER = {
  kunde_abgleich: "kanal_kunde_abgleich", kunde_terminlink: "kanal_kunde_terminlink",
  betrieb_meldung: "kanal_betrieb_meldung", betrieb_notfall: "kanal_betrieb_notfall",
} as const
function kanalWert(v: unknown, standard: Kanal): Kanal {
  return v === "aus" || v === "sms" || v === "whatsapp" || v === "email" ? v : standard
}
function KanalWahl({ wert, onChange, testid, mitEmail = false }: { wert: Kanal; onChange: (k: Kanal) => void; testid: string; mitEmail?: boolean }) {
  return (
    <select value={wert} onChange={(e) => onChange(kanalWert(e.target.value, "aus"))} className={inputCls}
      data-testid={testid} style={{ maxWidth: 260 }}>
      <option value="aus">Aus</option>
      <option value="sms">SMS</option>
      <option value="whatsapp">WhatsApp (SMS als Rückfall)</option>
      {(mitEmail || wert === "email") && <option value="email">E-Mail an die Betriebsadresse (SMS als Rückfall)</option>}
    </select>
  )
}

const inputCls = "w-full rounded-md border bg-background px-3 py-1.5 text-[13.5px] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30"

type SpamAutomatik = { an: boolean; regeln: Record<string, string>; global: string }

/**
 * Ein Schalter je Betrieb (Paul 16.10.2026): „Spam automatisch einsortieren".
 * AN = Spam und Werbung wandern beim Eingang in den Spam-Ordner des jeweiligen
 * Postfachs (auch im Mailprogramm sichtbar); was doch echt war, holt man dort
 * mit „Kein Spam" zurück. Der Server prüft beim Einschalten jedes Postfach und
 * legt „Spam" an, wenn es keinen Spam-Ordner gibt. Wirkt nur auf neue Mails.
 */
export function SpamAutomatikSection({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const key = ["spam-automatik", slug]
  const q = useQuery<SpamAutomatik>({
    queryKey: key,
    queryFn: async () => {
      const r = await fetch(`/api/v1/tenants/${encodeURIComponent(slug)}/spam-automatik`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null)
  const schalten = useMutation({
    mutationFn: async (an: boolean) => {
      const r = await fetch(`/api/v1/tenants/${encodeURIComponent(slug)}/spam-automatik`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ an }),
      })
      const d = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`)
      return d as { an: boolean; ordner: string | null }
    },
    onSuccess: (d) => {
      setMeldung({ ok: true, text: d.an ? `✓ An — Spam-Ordner: ${d.ordner || "?"}` : "✓ Aus — nichts wird mehr automatisch verschoben" })
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({ queryKey: ["strategie-archiv"] })
      qc.invalidateQueries({ queryKey: ["archiv-ziele", slug] })
    },
    onError: (e) => setMeldung({ ok: false, text: e instanceof Error ? e.message : String(e) }),
  })
  const an = !!q.data?.an
  return (
    <Section title="Spam automatisch einsortieren" icon="🛡️" configured={an}>
      <label className="flex items-start gap-3 cursor-pointer" data-testid="spam-automatik">
        <input type="checkbox" className="mt-1" checked={an} disabled={q.isLoading || schalten.isPending}
               aria-label="Spam automatisch einsortieren"
               onChange={(e) => { setMeldung(null); schalten.mutate(e.target.checked) }} />
        <span className="text-[13px]">
          <span className="font-semibold">{schalten.isPending ? "Schalte…" : an ? "An" : "Aus"}</span>
          <span className="block text-[12px] text-muted-foreground">
            Neue Spam- und Werbe-Mails landen sofort im Spam-Ordner des jeweiligen Postfachs — auch im Mailprogramm.
            Nie verschoben: DATEV, Rechnungen/Mahnungen/Anfragen im Betreff, Antworten, eigene Adressen,
            Absender mit „Kein Spam“ oder denen ihr schon geschrieben habt. Im Posteingang links unter „Spam“
            durchsehen, Fehlgriffe mit „Kein Spam“ zurückholen — das merkt sich die Plattform.
          </span>
        </span>
      </label>
      {q.error && <div className="text-[12px] text-red-600">Nicht geladen: {String(q.error)}</div>}
      {q.data && q.data.global !== "an" && (
        <div className="text-[12px] text-amber-700">Achtung: Die Archiv-Automatik ist serverweit auf „{q.data.global}“ gestellt — der Schalter wirkt erst, wenn sie wieder an ist.</div>
      )}
      {meldung && <div className={cn("text-[12px] font-semibold", meldung.ok ? "text-success" : "text-red-600")}>{meldung.text}</div>}
    </Section>
  )
}

type ArchivZielArt = {
  art: "werbung" | "archiv"; label: string; eingestellt: string | null; vorbelegt: string[]; gefunden: string | null
  regeln: { regel: string; label: string; modus: string }[]
}
type ArchivZiele = { arten: ArchivZielArt[]; ordner: string[]; ordnerliste_fehler: string | null }

/**
 * Archiv-Automatik (Baustein 1): wohin die Regeln verschieben, je Betrieb.
 * Paul 27.09.2026: gehört in die Tenant-Konfiguration. Der Server prüft jeden
 * Namen gegen die Ordnerliste des Postfachs; ohne vorhandenen Ordner lässt er
 * keine Regel scharf schalten. Schalter, Ampel und Stichprobe bleiben im
 * Projektplan (Verbessern → Projektplan).
 */
export function ArchivZieleSection({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const q = useQuery<ArchivZiele>({
    queryKey: ["archiv-ziele", slug],
    queryFn: async () => {
      const r = await fetch(`/api/v1/tenants/${encodeURIComponent(slug)}/archiv-ziele`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const [werte, setWerte] = useState<Record<string, string>>({})
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null)
  useEffect(() => {
    if (q.data) setWerte(Object.fromEntries(q.data.arten.map((a) => [a.art, a.eingestellt || ""])))
  }, [q.data])
  const speichern = useMutation({
    mutationFn: async (b: { art: string; ordner: string }) => {
      const r = await fetch(`/api/v1/tenants/${encodeURIComponent(slug)}/archiv-ziele`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(b),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d?.detail || `HTTP ${r.status}`)
      }
      return r.json()
    },
    onSuccess: () => {
      setMeldung({ ok: true, text: "✓ Zielordner gespeichert" })
      qc.invalidateQueries({ queryKey: ["archiv-ziele", slug] })
      qc.invalidateQueries({ queryKey: ["strategie-archiv"] })
    },
    onError: (e) => setMeldung({ ok: false, text: e instanceof Error ? e.message : String(e) }),
  })
  const d = q.data
  const eingestellt = !!d?.arten.some((a) => a.eingestellt)
  return (
    <Section title="Archiv-Automatik: Zielordner" icon="🗂️" configured={eingestellt}>
      <div className="text-[12px] text-muted-foreground">
        Wohin die Archiv-Automatik Mails verschiebt. Leer = Vorbelegung. Der Ordner muss im Postfach existieren —
        sonst lässt sich keine Regel scharf schalten. Schalter und Stichprobe: Verbessern → Projektplan.
      </div>
      {q.isLoading && <div className="text-[12px] text-muted-foreground">Lädt Ordner aus dem Postfach…</div>}
      {q.error && <div className="text-[12px] text-red-600">Nicht geladen: {String(q.error)}</div>}
      {d?.ordnerliste_fehler && <div className="text-[12px] text-amber-700">{d.ordnerliste_fehler}</div>}
      {d && (
        <datalist id={`archiv-ordner-${slug}`}>{d.ordner.map((o) => <option key={o} value={o} />)}</datalist>
      )}
      {d?.arten.map((a) => {
        const wert = werte[a.art] ?? ""
        const geaendert = wert.trim() !== (a.eingestellt || "")
        const scharf = a.regeln.filter((r) => r.modus === "scharf")
        return (
          <div key={a.art} className="border-t pt-2" data-testid={`archiv-ziel-${a.art}`}>
            <F label={a.label} hint={`Vorbelegung: ${a.vorbelegt.join(" / ")} · Regeln: ${a.regeln.map((r) => r.label).join(", ")}`}>
              <div className="flex items-center gap-2">
                <input className={inputCls} list={`archiv-ordner-${slug}`} value={wert} placeholder={a.vorbelegt[0] || ""}
                       aria-label={`Zielordner ${a.label}`} onChange={(e) => setWerte({ ...werte, [a.art]: e.target.value })} />
                <button type="button" className="pp-btn pp-btn-sm pp-btn-primary" disabled={!geaendert || speichern.isPending}
                        onClick={() => { setMeldung(null); speichern.mutate({ art: a.art, ordner: wert.trim() }) }}>
                  Speichern
                </button>
              </div>
            </F>
            <div className="text-[11.5px] mt-1">
              {a.gefunden
                ? <span className="text-success">Im Postfach: {a.gefunden}</span>
                : <span className="text-red-600">Kein passender Ordner im Postfach</span>}
              {scharf.length > 0 && <span className="text-muted-foreground"> · scharf: {scharf.map((r) => r.label).join(", ")}</span>}
            </div>
          </div>
        )
      })}
      {meldung && <div className={cn("text-[12px] font-semibold", meldung.ok ? "text-success" : "text-red-600")}>{meldung.text}</div>}
    </Section>
  )
}

type Postfach = { id: number; adresse: string; bezeichnung: string; imap_folder: string; aktiv: boolean; passwort_gesetzt: boolean }
type Postfaecher = { haupt: { adresse: string; bezeichnung: string }; weitere: Postfach[] }
type MailTestRes = { ok: boolean; imap?: { ok: boolean; message: string }; smtp?: { ok: boolean; message: string } }

/**
 * Weitere Postfächer (Berggötz Holzbau, 29.09.2026). Das Hauptpostfach steht
 * oben im Abschnitt E-Mail; hier kommen nur die zusätzlichen dazu. Host,
 * Anmeldeverfahren und die Microsoft-App gelten für alle gemeinsam — pro
 * Postfach nur Adresse und Anzeigename. Im Posteingang erscheinen alle Mails
 * zusammen, jede mit ihrer Postfach-Kennung.
 */
export function PostfaecherSection({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const key = ["postfaecher", slug]
  const basis = `/api/v1/tenants/${encodeURIComponent(slug)}/postfaecher`
  const q = useQuery<Postfaecher>({
    queryKey: key,
    queryFn: async () => {
      const r = await fetch(basis, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })
  const [neu, setNeu] = useState({ adresse: "", bezeichnung: "", imap_password: "" })
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null)
  const [tests, setTests] = useState<Record<number, { running?: boolean; res?: MailTestRes }>>({})
  const [pw, setPw] = useState<Record<number, string>>({})

  const aufruf = async (url: string, method: string, body?: unknown) => {
    const r = await fetch(url, {
      method, credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    })
    if (!r.ok) {
      const d = await r.json().catch(() => ({}))
      const detail = Array.isArray(d?.detail) ? d.detail.map((x: { msg?: string }) => x.msg).join(", ") : d?.detail
      throw new Error(detail || `HTTP ${r.status}`)
    }
    return r.json()
  }
  const fertig = (text: string) => { setMeldung({ ok: true, text }); qc.invalidateQueries({ queryKey: key }) }
  const fehler = (e: unknown) => setMeldung({ ok: false, text: e instanceof Error ? e.message : String(e) })

  const anlegen = useMutation({
    mutationFn: () => aufruf(basis, "POST", {
      adresse: neu.adresse.trim(), bezeichnung: neu.bezeichnung.trim(),
      imap_password: neu.imap_password || null,
    }),
    onSuccess: () => { setNeu({ adresse: "", bezeichnung: "", imap_password: "" }); fertig("✓ Postfach angebunden") },
    onError: fehler,
  })
  const aendern = useMutation({
    mutationFn: (b: { id: number; patch: Partial<Postfach> & { imap_password?: string } }) =>
      aufruf(`${basis}/${b.id}`, "PATCH", b.patch),
    onSuccess: (_d, b) => {
      if (b.patch.imap_password) setPw((p) => ({ ...p, [b.id]: "" }))
      fertig("✓ Gespeichert")
    },
    onError: fehler,
  })
  const entfernen = useMutation({
    mutationFn: (id: number) => aufruf(`${basis}/${id}`, "DELETE"),
    onSuccess: () => fertig("✓ Anbindung entfernt — abgeholte Mails bleiben im Portal"),
    onError: fehler,
  })
  const testen = async (id: number) => {
    setTests((t) => ({ ...t, [id]: { running: true } }))
    try {
      const res = await aufruf(`/api/v1/tenants/${encodeURIComponent(slug)}/test-mail-connection?postfach_id=${id}`, "POST")
      setTests((t) => ({ ...t, [id]: { res } }))
    } catch (e) {
      setTests((t) => ({ ...t, [id]: { res: { ok: false, imap: { ok: false, message: String(e) } } } }))
    }
  }

  const d = q.data
  return (
    <Section title="Weitere Postfächer" icon="📬" configured={!!d?.weitere.length}>
      <div className="text-[12px] text-muted-foreground">
        Zusätzliche Postfächer desselben Betriebs. Server, Anmeldeverfahren und Microsoft-App gelten wie beim
        Hauptpostfach{d?.haupt.adresse ? ` (${d.haupt.adresse})` : ""}. Im Posteingang erscheinen alle Mails
        zusammen, jede mit ihrer Postfach-Kennung. Antworten gehen aus dem Postfach, in dem die Mail liegt.
      </div>
      {q.isLoading && <div className="text-[12px] text-muted-foreground">Lädt…</div>}
      {q.error && <div className="text-[12px] text-red-600">Nicht geladen: {String(q.error)}</div>}
      {d?.weitere.map((pf) => (
        <div key={pf.id} className="border-t pt-2 space-y-1" data-testid={`postfach-${pf.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium">{pf.adresse}</span>
            {!pf.aktiv && <span className="text-[11px] text-amber-700">pausiert</span>}
            {!pf.passwort_gesetzt && <span className="text-[11px] text-amber-700">Passwort fehlt</span>}
            <input className={cn(inputCls, "max-w-[180px]")} defaultValue={pf.bezeichnung} placeholder="Anzeigename"
                   aria-label={`Anzeigename ${pf.adresse}`}
                   onBlur={(e) => { if (e.target.value.trim() !== pf.bezeichnung) aendern.mutate({ id: pf.id, patch: { bezeichnung: e.target.value.trim() } }) }} />
            <button type="button" className="rounded-md border px-2 py-1 text-xs hover:bg-secondary"
                    onClick={() => aendern.mutate({ id: pf.id, patch: { aktiv: !pf.aktiv } })}>
              {pf.aktiv ? "Pausieren" : "Aktivieren"}
            </button>
            <button type="button" className="rounded-md border px-2 py-1 text-xs hover:bg-secondary" onClick={() => testen(pf.id)}>
              {tests[pf.id]?.running ? "Teste…" : "Verbindung testen"}
            </button>
            <button type="button" className="rounded-md border px-2 py-1 text-xs text-red-600 hover:bg-secondary"
                    onClick={() => { if (window.confirm(`Anbindung von ${pf.adresse} entfernen? Bereits abgeholte Mails bleiben im Portal.`)) entfernen.mutate(pf.id) }}>
              Entfernen
            </button>
          </div>
          {/* Passwort nachtragen/aendern (Berggoetz 05.10.2026: Postfaecher werden
              pausiert vorbereitet, das Buero traegt nur noch das Passwort ein).
              Pausiert → speichern UND aktivieren in einem Schritt. */}
          <div className="flex flex-wrap items-center gap-2">
            <input type="password" className={cn(inputCls, "max-w-[220px]")} value={pw[pf.id] || ""}
                   placeholder={pf.passwort_gesetzt ? "••••••••  (nur bei Änderung)" : "Passwort eingeben"}
                   aria-label={`Passwort ${pf.adresse}`} autoComplete="new-password"
                   onChange={(e) => setPw((p) => ({ ...p, [pf.id]: e.target.value }))} />
            <button type="button" className="rounded-md border px-2 py-1 text-xs hover:bg-secondary"
                    disabled={!pw[pf.id] || aendern.isPending}
                    onClick={() => { setMeldung(null); aendern.mutate({ id: pf.id, patch: pf.aktiv ? { imap_password: pw[pf.id] } : { imap_password: pw[pf.id], aktiv: true } }) }}>
              {pf.aktiv ? "Passwort speichern" : "Passwort speichern und aktivieren"}
            </button>
          </div>
          {tests[pf.id]?.res && (
            <div className="text-[11.5px] space-y-0.5">
              {(["imap", "smtp"] as const).map((k) => tests[pf.id]?.res?.[k] && (
                <div key={k} className="flex gap-2">
                  <span className={tests[pf.id]?.res?.[k]?.ok ? "text-green-600" : "text-red-600"}>{tests[pf.id]?.res?.[k]?.ok ? "✓" : "✗"}</span>
                  <span className="font-medium uppercase">{k}</span>
                  <span className="text-muted-foreground break-all">{tests[pf.id]?.res?.[k]?.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="border-t pt-2 flex flex-wrap items-end gap-2">
        <F label="Adresse">
          <input type="email" className={inputCls} value={neu.adresse} placeholder="rechnung@firma.de" aria-label="Neue Postfach-Adresse"
                 onChange={(e) => setNeu({ ...neu, adresse: e.target.value })} />
        </F>
        <F label="Anzeigename">
          <input className={inputCls} value={neu.bezeichnung} placeholder="Rechnungen" aria-label="Neuer Anzeigename"
                 onChange={(e) => setNeu({ ...neu, bezeichnung: e.target.value })} />
        </F>
        <F label="Passwort" hint="Nur bei Passwort-Anmeldung und eigenem Passwort. Bei Microsoft 365 leer lassen.">
          <input type="password" className={inputCls} value={neu.imap_password} aria-label="Neues Postfach-Passwort"
                 onChange={(e) => setNeu({ ...neu, imap_password: e.target.value })} />
        </F>
        <button type="button" className="pp-btn pp-btn-sm pp-btn-primary" disabled={!neu.adresse.trim() || anlegen.isPending}
                onClick={() => { setMeldung(null); anlegen.mutate() }}>
          Postfach hinzufügen
        </button>
      </div>
      {meldung && <div className={cn("text-[12px] font-semibold", meldung.ok ? "text-success" : "text-red-600")}>{meldung.text}</div>}
    </Section>
  )
}

function Section({ title, children, icon, configured, defaultOpen = false }: {
  title: string;
  children: React.ReactNode;
  icon?: string;
  configured?: boolean;
  defaultOpen?: boolean;
}) {
  return (
    <details open={defaultOpen} className="rounded-md border bg-card mb-3">
      <summary className="flex items-center gap-2 cursor-pointer px-4 py-3 select-none hover:bg-secondary/40" style={{ listStyle: "none" }}>
        <span style={{ fontSize: 16 }}>{icon || "⚙️"}</span>
        <span className="text-[13px] font-semibold uppercase tracking-wider flex-1">{title}</span>
        {configured !== undefined && (
          <span
            style={{
              width: 8, height: 8, borderRadius: "50%",
              background: configured ? "#10b981" : "#cbd5e1",
              flexShrink: 0,
            }}
            title={configured ? "Konfiguriert" : "Nicht konfiguriert"}
          />
        )}
        <span className="text-[10px] text-muted-foreground" style={{ width: 14, textAlign: "center" }}>▾</span>
      </summary>
      <div className="px-4 pb-4 pt-1 space-y-3">{children}</div>
    </details>
  )
}

function SignatureLogoUploader({ slug, logoSet, onChange }: { slug: string; logoSet: boolean; onChange: () => void }) {
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState("")
  const [bust, setBust] = useState(0)

  const upload = async (file: File) => {
    setUploading(true); setErr("")
    try {
      const fd = new FormData()
      fd.append("logo", file)
      const r = await fetch(`/api/v1/tenants/${slug}/signature-logo`, {
        method: "POST", credentials: "include", body: fd,
      })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j.detail || `HTTP ${r.status}`)
      }
      setBust(Date.now())
      onChange()
    } catch (e: any) {
      setErr(e.message || "Upload fehlgeschlagen")
    } finally {
      setUploading(false)
    }
  }

  const remove = async () => {
    if (!confirm("Logo wirklich entfernen?")) return
    setUploading(true); setErr("")
    try {
      const r = await fetch(`/api/v1/tenants/${slug}/signature-logo`, {
        method: "DELETE", credentials: "include",
      })
      if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`)
      setBust(Date.now())
      onChange()
    } catch (e: any) {
      setErr(e.message || "Entfernen fehlgeschlagen")
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          disabled={uploading}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = "" }}
          className="text-[12px]"
        />
        {logoSet && (
          <button type="button" onClick={remove} disabled={uploading} className="px-2 py-1 text-[12px] rounded border hover:bg-muted">
            Entfernen
          </button>
        )}
      </div>
      {logoSet && (
        <img
          src={`/api/v1/tenants/${slug}/signature-logo${bust ? `?v=${bust}` : ""}`}
          alt="Logo"
          style={{ maxWidth: 200, maxHeight: 80, width: "auto", height: "auto", border: "1px solid #e2e8f0", borderRadius: 4, background: "#fff", padding: 4 }}
        />
      )}
      {err && <div className="text-[12px] text-red-600">{err}</div>}
    </div>
  )
}

type SigBild = { name: string; hoehe?: number | null }

function SignaturBilder({
  slug, namen, onChange, einfuegen,
}: { slug: string; namen: SigBild[]; onChange: () => void; einfuegen: (platzhalter: string) => void }) {
  const [name, setName] = useState("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState("")
  const [bust, setBust] = useState(0)

  const hochladen = async (datei: File) => {
    const n = (name || datei.name.replace(/\.[^.]+$/, "")).toLowerCase().replace(/[^a-z0-9_-]/g, "")
    if (!n) { setErr("Bitte einen Namen vergeben (Buchstaben/Ziffern)"); return }
    setBusy(true); setErr("")
    try {
      const fd = new FormData()
      fd.append("bild", datei)
      const r = await fetch(`/api/v1/tenants/${slug}/signature-images/${n}`, {
        method: "POST", credentials: "include", body: fd,
      })
      if (!r.ok) {
        const j = await r.json().catch(() => ({}))
        throw new Error(j.detail || `HTTP ${r.status}`)
      }
      setName(""); setBust(Date.now()); onChange()
      // Direkt an der Cursorposition einsetzen — sonst muesste man den
      // Platzhalter abtippen und ein Tippfehler bliebe unbemerkt.
      einfuegen(`{{BILD:${n}}}`)
    } catch (e: any) {
      setErr(e.message || "Upload fehlgeschlagen")
    } finally { setBusy(false) }
  }

  const hoeheSetzen = async (n: string, h: number) => {
    if (!Number.isFinite(h) || h < 10 || h > 200) { setErr("Höhe: 10 bis 200 px"); return }
    setBusy(true); setErr("")
    try {
      const r = await fetch(`/api/v1/tenants/${slug}/signature-images/${n}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hoehe: h }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      onChange()
    } catch (e: any) {
      setErr(e.message || "Höhe konnte nicht gesetzt werden")
    } finally { setBusy(false) }
  }

  const entfernen = async (n: string) => {
    if (!confirm(`Bild "${n}" entfernen? Der Platzhalter {{BILD:${n}}} muss dann auch raus.`)) return
    setBusy(true); setErr("")
    try {
      const r = await fetch(`/api/v1/tenants/${slug}/signature-images/${n}`, {
        method: "DELETE", credentials: "include",
      })
      if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`)
      setBust(Date.now()); onChange()
    } catch (e: any) {
      setErr(e.message || "Entfernen fehlgeschlagen")
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-2">
      {namen.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {namen.map((b) => {
            const n = b.name
            return (
            <div key={n} className="border rounded p-2 flex flex-col items-center gap-1" style={{ width: 160 }}>
              <img
                src={`/api/v1/tenants/${slug}/signature-images/${n}${bust ? `?v=${bust}` : ""}`}
                alt={n}
                style={{ maxWidth: 140, maxHeight: 70, width: "auto", height: "auto" }}
              />
              <code className="text-[10px]">{`{{BILD:${n}}}`}</code>
              <label className="text-[11px] flex items-center gap-1">
                Höhe
                <input
                  type="number" min={10} max={200}
                  defaultValue={b.hoehe ?? 45}
                  disabled={busy}
                  className="border rounded px-1 py-0.5 text-[11px]"
                  style={{ width: 54 }}
                  onBlur={(e) => hoeheSetzen(n, parseInt(e.target.value))}
                />
                px
              </label>
              <div className="flex gap-2">
                <button type="button" className="text-[11px] underline" onClick={() => einfuegen(`{{BILD:${n}}}`)}>
                  einfügen
                </button>
                <button type="button" className="text-[11px] underline text-destructive" disabled={busy} onClick={() => entfernen(n)}>
                  entfernen
                </button>
              </div>
            </div>
          )})}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          className={inputCls}
          style={{ maxWidth: 160 }}
          placeholder="Name, z.B. bni"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          disabled={busy}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) hochladen(f); e.target.value = "" }}
        />
      </div>
      {err && <div className="text-xs text-destructive">{err}</div>}
    </div>
  )
}


function SignaturePreview({ slug, html, logoSet, bilder }: { slug: string; html: string; logoSet: boolean; bilder?: SigBild[] }) {
  // Platzhalter durch die echten Bilder ersetzen, damit die Vorschau zeigt,
  // was ankommt — sonst steht dort woertlich {{BILD:bni}} und man merkt einen
  // Tippfehler im Namen erst beim Empfaenger.
  const htmlRendered = (html || "")
    .replace(/\n/g, "<br>")
    .replace(/\{\{BILD:([A-Za-z0-9_-]{1,24})\}\}/g, (_m, n) => {
      // Gleiche Hoehe wie im Versand (BILD_HOEHE in services/imap/_send.py),
      // sonst zeigt die Vorschau andere Groessenverhaeltnisse als die Mail.
      const treffer = (bilder || []).find((b) => b.name === n)
      const bild = (q: string, h: number) => `<img src="${q}" alt="${n}" style="height:${h}px;width:auto">`
      if (treffer) return bild(`/api/v1/tenants/${slug}/signature-images/${n}`, treffer.hoehe || 45)
      // `logo` ist reserviert: greift auf das alte Einzel-Logo-Feld zurueck.
      if (n === "logo" && logoSet) return bild(`/api/v1/tenants/${slug}/signature-logo`, 45)
      return `<span style="color:#b91c1c">[kein Bild „${n}" hinterlegt]</span>`
    })
  return (
    <div
      style={{
        borderTop: "1px solid #ddd", paddingTop: 10, marginTop: 4,
        fontSize: 14, color: "#333", fontFamily: "Arial, sans-serif", background: "#fff",
        padding: "10px 12px", borderRadius: 4,
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: htmlRendered || "<em style='color:#999'>Keine Signatur</em>" }} />
      {/* Nur anhaengen, wenn es nicht schon per {{BILD:logo}} im Text steht —
          sonst zeigt die Vorschau das Logo doppelt, die Mail aber einmal. */}
      {logoSet && !(html || "").includes("{{BILD:logo}}") && (
        <>
          <br />
          <img
            src={`/api/v1/tenants/${slug}/signature-logo`}
            alt="Logo"
            style={{ maxWidth: 200, maxHeight: 80, width: "auto", height: "auto", display: "inline-block", marginTop: 4 }}
          />
        </>
      )}
    </div>
  )
}


function AdvancedSection({ slug }: { slug: string }) {
  const qc = useQueryClient()
  const [, setSaveOk] = useState(false)
  const [checkIds, setCheckIds] = useState<Set<number>>(new Set())
  const [calendarMode, setCalendarMode] = useState<string>("union")
  const [voiceSlug, setVoiceSlug] = useState<string>("")
  const [smartInbox, setSmartInbox] = useState<SmartInboxConfig>(defaultSmartInbox())
  const [heroTypeId, setHeroTypeId] = useState<string>("")
  const [heroMeasureId, setHeroMeasureId] = useState<string>("")
  const [dirty, setDirty] = useState(false)

  const { data, isLoading, refetch } = useQuery<AdvancedData>({
    queryKey: ["tenant-advanced", slug],
    queryFn: async () => {
      const res = await fetch(`/api/v1/tenants/${slug}/advanced`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  useEffect(() => {
    if (!data) return
    setCheckIds(new Set(data.check_monteur_ids))
    setCalendarMode(data.calendar_mode || "union")
    setVoiceSlug(data.current_voice_slug || "")
    setSmartInbox(data.smart_inbox || defaultSmartInbox())
    setHeroTypeId(data.hero_project_type_id ? String(data.hero_project_type_id) : "")
    setHeroMeasureId(data.hero_measure_id ? String(data.hero_measure_id) : "")
    setDirty(false)
  }, [data])

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/v1/tenants/${slug}/advanced`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          check_monteur_ids: Array.from(checkIds),
          calendar_mode: calendarMode,
          forward_rules: data?.forward_rules || [],
          doc_triggers: data?.doc_triggers || {},
          smart_inbox: smartInbox,
          hero_project_type_id: heroTypeId ? parseInt(heroTypeId) : null,
          hero_measure_id: heroMeasureId ? parseInt(heroMeasureId) : null,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    },
    onSuccess: () => {
      setSaveOk(true)
      setDirty(false)
      setTimeout(() => setSaveOk(false), 2000)
      qc.invalidateQueries({ queryKey: ["tenant-advanced", slug] })
    },
  })

  const [linkMsg, setLinkMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const linkVoice = useMutation({
    mutationFn: async (newSlug: string) => {
      const res = await fetch(`/api/v1/tenants/${slug}/link-voice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ voice_tenant_slug: newSlug }),
      })
      if (!res.ok) {
        const txt = await res.text().catch(() => "")
        throw new Error(`HTTP ${res.status}: ${txt.slice(0, 200)}`)
      }
    },
    onSuccess: () => {
      setLinkMsg({ ok: true, text: "✓ Voice-Agent verbunden" })
      setTimeout(() => setLinkMsg(null), 3000)
      refetch()
    },
    onError: (err: any) => {
      setLinkMsg({ ok: false, text: "✗ " + (err?.message || "Verbinden fehlgeschlagen") })
    },
  })

  if (isLoading) return <div className="text-[12px] text-muted-foreground">Lädt Advanced-Config...</div>
  if (!data) return null

  return (
    <>
      <Section title="Voice-Agent-Zuordnung">
        {data.voice_tenants.length === 0 ? (
          <div className="text-[12px] text-muted-foreground">
            Keine Voice-Tenants verfügbar (Voice-Agent nicht erreichbar).
          </div>
        ) : (
          <>
            <div>
              <label className="text-[11px] font-semibold text-muted-foreground block mb-1">
                Verbundener Voice-Tenant
              </label>
              <select
                value={voiceSlug}
                onChange={(e) => setVoiceSlug(e.target.value)}
                className={inputCls}
              >
                <option value="">— keine Zuordnung —</option>
                {data.voice_tenants.map((vt: any) => (
                  <option key={vt.slug || vt.tenant_slug} value={vt.slug || vt.tenant_slug}>
                    {vt.name || vt.slug || vt.tenant_slug}
                    {vt.portal_tenant_slug && vt.portal_tenant_slug !== slug ? ` (bereits verbunden mit ${vt.portal_tenant_slug})` : ""}
                  </option>
                ))}
              </select>
            </div>
            {(() => {
              const cur = data.current_voice_slug || ""
              const unchanged = voiceSlug === cur
              const isUnbind = cur && !voiceSlug
              const label = linkVoice.isPending
                ? (isUnbind ? "Entbindet..." : "Verbindet...")
                : (isUnbind ? "Voice-Agent entbinden" : voiceSlug ? "Voice-Agent verbinden" : "Keine Änderung")
              return (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => linkVoice.mutate(voiceSlug)}
                    disabled={unchanged || linkVoice.isPending}
                    className={cn(
                      "pp-btn pp-btn-sm",
                      isUnbind ? "border border-red-500 text-red-600 hover:bg-red-50" : "pp-btn-primary",
                      (unchanged || linkVoice.isPending) && "opacity-50 cursor-not-allowed",
                    )}
                  >
                    {label}
                  </button>
                  {cur && !isUnbind && voiceSlug === cur && (
                    <button
                      type="button"
                      onClick={() => { setVoiceSlug(""); }}
                      className="pp-btn pp-btn-sm border text-red-600 hover:bg-red-50"
                    >
                      Verbindung loesen
                    </button>
                  )}
                </div>
              )
            })()}
            {linkMsg && (
              <span className={cn("ml-2 text-[12px] font-semibold", linkMsg.ok ? "text-success" : "text-red-600")}>
                {linkMsg.text}
              </span>
            )}
            {data.voice_agent_url && (
              <div className="text-[11.5px] text-muted-foreground mt-2">
                Konfiguration (Prompt/Stimme) auf <a href={`${data.voice_agent_url}/admin/`} target="_blank" rel="noopener" className="underline text-primary">Voice-Agent-Portal</a>.
              </div>
            )}
          </>
        )}
      </Section>

      <Section title="E-Mail Quick-Weiterleiten-Buttons">
        <div className="p-3 rounded-md" style={{ background: "#EEEDFE", fontSize: 12.5, color: "#3C3489" }}>
          Forward-Regeln werden jetzt im <a href="/context" style={{ textDecoration: "underline", fontWeight: 500 }}>Kontext-Center</a> gepflegt
          (zusammen mit Tonfall, KI-Regeln, Eskalations-Themen).
        </div>
      </Section>

      <Section title="Dokument-Trigger">
        <div className="p-3 rounded-md" style={{ background: "#EEEDFE", fontSize: 12.5, color: "#3C3489" }}>
          Dokument-Trigger werden jetzt im <a href="/context" style={{ textDecoration: "underline", fontWeight: 500 }}>Kontext-Center</a> → Tab „Pipeline" gepflegt
          (Card „Status nach Dokument-Erstellung", pro Projekttyp).
        </div>
      </Section>

      <ProjectTypesSection
        slug={slug}
        types={data.hero_project_types}
        measures={data.hero_measures}
        heroTypeId={heroTypeId}
        heroMeasureId={heroMeasureId}
        onTypeChange={(v) => { setHeroTypeId(v); setDirty(true) }}
        onMeasureChange={(v) => { setHeroMeasureId(v); setDirty(true) }}
        onReload={() => refetch()}
      />

      <Section title="Smart Inbox Schwellenwerte + Pipeline-Regeln">
        <div className="p-3 rounded-md" style={{ background: "#EEEDFE", fontSize: 12.5, color: "#3C3489" }}>
          <strong>Diese Konfiguration ist umgezogen.</strong><br />
          <strong>Schwellenwerte</strong> (Verpasste Anrufe / Kein Kontakt / Kein Projekt / E-Mail unbeantwortet / Urgent-Keywords)
          und <strong>Pipeline-Regeln pro Projekttyp</strong> werden im zentralen{" "}
          <a href="/context" style={{ textDecoration: "underline", fontWeight: 500 }}>Kontext-Center</a> gepflegt
          (Tab "Kommunikation" bzw. "Pipeline").
        </div>
      </Section>

      {dirty && (
        <div className="sticky bottom-0 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 text-[12.5px] flex items-center gap-2">
          <span className="flex-1">Änderungen in Advanced-Sections.</span>
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="pp-btn pp-btn-sm pp-btn-primary"
          >
            {save.isPending ? "Speichert..." : "Advanced speichern"}
          </button>
        </div>
      )}
    </>
  )
}

type AdvancedData = {
  monteure_per_tenant: Array<{
    tenant_slug: string
    tenant_name: string
    monteure: Array<{ id: number; name: string; hero_partner_id?: number }>
  }>
  tenant_monteure: Array<{ id: number; name: string; hero_partner_id?: number; primary: boolean; hero_ical_url: string }>
  check_monteur_ids: number[]
  voice_tenants: any[]
  voice_agent_url: string
  current_voice_slug: string
  calendar_mode: string
  forward_rules: Array<{ from_domain?: string; to_email?: string; [k: string]: any }>
  doc_triggers: Record<string, any>
  smart_inbox: SmartInboxConfig
  hero_project_types: HeroProjectType[]
  hero_measures: Array<{ id: number; name: string }>
  hero_type_names: Record<string, string>
  hero_project_type_id: number | null
  hero_measure_id: number | null
  calendar_step_mapping: Record<string, Record<string, number>>
}

type HeroProjectType = {
  id: number
  name: string
  status_code?: number
  steps?: Array<{ id: number; name: string; status_code?: number }>
}

type PipelineRule = {
  step_id: number | string
  name: string
  threshold: number
  unit: "h" | "d"
  priority: "low" | "medium" | "high" | "critical"
  enabled: boolean
}

type SmartInboxConfig = {
  enabled: boolean
  pipeline_rules_by_type: Record<string, PipelineRule[]>
  call_missed_hours: number
  call_no_contact_hours: number
  call_no_project_hours: number
  email_unanswered_hours: number
  email_urgent_keywords: string
  ai_rules: string[]
}

function defaultSmartInbox(): SmartInboxConfig {
  return {
    enabled: false,
    pipeline_rules_by_type: {},
    call_missed_hours: 1,
    call_no_contact_hours: 2,
    call_no_project_hours: 4,
    email_unanswered_hours: 24,
    email_urgent_keywords: "dringend,notfall,wasserschaden",
    ai_rules: [],
  }
}

function ProjectTypesSection({
  slug, types, measures, heroTypeId, heroMeasureId, onTypeChange, onMeasureChange, onReload,
}: {
  slug: string
  types: HeroProjectType[]
  measures: Array<{ id: number; name: string }>
  heroTypeId: string
  heroMeasureId: string
  onTypeChange: (v: string) => void
  onMeasureChange: (v: string) => void
  onReload: () => void
}) {
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState<string | null>(null)

  const runImport = async () => {
    setImporting(true); setImportMsg(null)
    try {
      const res = await fetch(`/api/v1/tenants/${slug}/import-project-types`, {
        method: "POST", credentials: "include",
      })
      const d = await res.json()
      if (!res.ok || d.error) throw new Error(d.error || `HTTP ${res.status}`)
      setImportMsg(`✓ ${d.imported_types} Types + ${d.imported_measures} Gewerke importiert`)
      onReload()
    } catch (e) {
      setImportMsg("Fehler: " + (e instanceof Error ? e.message : String(e)))
    } finally {
      setImporting(false)
    }
  }

  return (
    <Section title="HERO Projekt-Types + Gewerke">
      <div className="text-[12px] text-muted-foreground">
        Projekt-Types und Gewerke werden aus HERO importiert und dienen als Fallback wenn
        Kontakte/Projekte ohne explizite Zuordnung angelegt werden.
      </div>
      <div className="flex gap-2 items-center">
        <button
          type="button"
          onClick={runImport}
          disabled={importing}
          className={cn("pp-btn pp-btn-sm", importing && "opacity-50 cursor-not-allowed")}
        >
          {importing ? "Importiert..." : "↓ Aus HERO importieren"}
        </button>
        {importMsg && (
          <span className={cn("text-[12px]", importMsg.startsWith("✓") ? "text-success" : "text-destructive")}>
            {importMsg}
          </span>
        )}
      </div>

      <div>
        <label className="text-[11px] font-semibold text-muted-foreground block mb-1">
          Standard-Projekttyp ({types.length} verfügbar)
        </label>
        <select value={heroTypeId} onChange={(e) => onTypeChange(e.target.value)} className={inputCls}>
          <option value="">— kein Default —</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>{t.name} (ID {t.id})</option>
          ))}
        </select>
      </div>

      <div>
        <label className="text-[11px] font-semibold text-muted-foreground block mb-1">
          Standard-Gewerk ({measures.length} verfügbar)
        </label>
        <select value={heroMeasureId} onChange={(e) => onMeasureChange(e.target.value)} className={inputCls}>
          <option value="">— kein Default —</option>
          {measures.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
      </div>

      {types.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary className="text-[12px] text-muted-foreground cursor-pointer">
            Alle importierten Types anzeigen ({types.length})
          </summary>
          <div className="mt-2 space-y-1 max-h-40 overflow-y-auto rounded-md border p-2">
            {types.map((t) => (
              <div key={t.id} className="text-[12px]">
                <strong>{t.name}</strong>
                <span className="text-muted-foreground ml-2">#{t.id}</span>
                {t.steps && t.steps.length > 0 && (
                  <span className="text-muted-foreground ml-2">· {t.steps.length} Steps</span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}
    </Section>
  )
}

// @ts-expect-error legacy section, ersetzt durch Kontext-Center, dead-code aber behalten
function SmartInboxSection({
  smartInbox, heroTypes, typeNames, onChange,
}: {
  smartInbox: SmartInboxConfig
  heroTypes: HeroProjectType[]
  typeNames: Record<string, string>
  onChange: (si: SmartInboxConfig) => void
}) {
  const update = (patch: Partial<SmartInboxConfig>) => onChange({ ...smartInbox, ...patch })

  // Immer genau eine Rule pro step_id halten (Auto-Rendering aller Steps).
  // Wenn Step noch keine Rule hat: Default mit enabled=false.
  const getOrDefaultRule = (typeId: string, step: { id: number; name: string }): PipelineRule => {
    const list = smartInbox.pipeline_rules_by_type[typeId] || []
    const found = list.find((r) => String(r.step_id) === String(step.id))
    if (found) return found
    return { step_id: step.id, name: step.name, threshold: 3, unit: "d", priority: "medium", enabled: false }
  }

  const upsertRule = (typeId: string, step: { id: number; name: string }, patch: Partial<PipelineRule>) => {
    const list = smartInbox.pipeline_rules_by_type[typeId] || []
    const idx = list.findIndex((r) => String(r.step_id) === String(step.id))
    const base = idx >= 0 ? list[idx] : { step_id: step.id, name: step.name, threshold: 3, unit: "d" as const, priority: "medium" as const, enabled: false }
    const next: PipelineRule = { ...base, ...patch, step_id: step.id, name: step.name }
    const newList = idx >= 0 ? list.map((r, i) => i === idx ? next : r) : [...list, next]
    onChange({
      ...smartInbox,
      pipeline_rules_by_type: { ...smartInbox.pipeline_rules_by_type, [typeId]: newList },
    })
  }

  const bulkSetEnabled = (typeId: string, type: { id: number; steps?: { id: number; name: string }[] }, enabled: boolean) => {
    const list = smartInbox.pipeline_rules_by_type[typeId] || []
    const byId = new Map(list.map((r) => [String(r.step_id), r]))
    const newList: PipelineRule[] = (type.steps || []).map((s) => {
      const existing = byId.get(String(s.id))
      if (existing) return { ...existing, enabled }
      return { step_id: s.id, name: s.name, threshold: 3, unit: "d", priority: "medium", enabled }
    })
    onChange({
      ...smartInbox,
      pipeline_rules_by_type: { ...smartInbox.pipeline_rules_by_type, [typeId]: newList },
    })
  }

  return (
    <Section title="Smart Inbox Rules">
      <label className="flex items-center gap-2 cursor-pointer text-[13px]">
        <input
          type="checkbox"
          checked={smartInbox.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />
        <strong>Smart Inbox aktiviert</strong>
      </label>

      {smartInbox.enabled && (
        <>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <F label="Verpasste Anrufe nach (h)" hint="Nach wie vielen Stunden als dringend markieren">
              <input
                type="number" min={0}
                value={smartInbox.call_missed_hours}
                onChange={(e) => update({ call_missed_hours: parseInt(e.target.value) || 0 })}
                className={inputCls}
              />
            </F>
            <F label="Kein Kontakt zugeordnet (h)">
              <input
                type="number" min={0}
                value={smartInbox.call_no_contact_hours}
                onChange={(e) => update({ call_no_contact_hours: parseInt(e.target.value) || 0 })}
                className={inputCls}
              />
            </F>
            <F label="Kein Projekt zugeordnet (h)">
              <input
                type="number" min={0}
                value={smartInbox.call_no_project_hours}
                onChange={(e) => update({ call_no_project_hours: parseInt(e.target.value) || 0 })}
                className={inputCls}
              />
            </F>
            <F label="E-Mail unbeantwortet (h)">
              <input
                type="number" min={0}
                value={smartInbox.email_unanswered_hours}
                onChange={(e) => update({ email_unanswered_hours: parseInt(e.target.value) || 0 })}
                className={inputCls}
              />
            </F>
          </div>

          <F label="Urgent-Keywords" hint="Komma-getrennt. E-Mails mit diesen Wörtern werden sofort eskaliert.">
            <input
              value={smartInbox.email_urgent_keywords}
              onChange={(e) => update({ email_urgent_keywords: e.target.value })}
              className={inputCls}
            />
          </F>

          <div className="mt-3 p-3 rounded-md" style={{ background: "#EEEDFE", fontSize: 12.5, color: "#3C3489" }}>
            <strong>KI-Regeln, Tonfall, Eskalations-Themen, Phrasen + Forward-Regeln</strong><br />
            werden im zentralen <a href="/context" style={{ textDecoration: "underline", fontWeight: 500 }}>Kontext-Center</a> gepflegt.
          </div>

          <div className="mt-4">
            <div className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Pipeline-Regeln pro Projekttyp
            </div>
            {heroTypes.length === 0 ? (
              <div className="text-[12px] text-muted-foreground">
                Zuerst Projekt-Types aus HERO importieren (siehe Sektion oben).
              </div>
            ) : (
              <div className="space-y-2">
                {heroTypes.map((t) => {
                  const typeId = String(t.id)
                  const steps = t.steps || []
                  const enabledCount = steps.filter((s) => getOrDefaultRule(typeId, s).enabled).length
                  return (
                    <details key={t.id} className="rounded-md border">
                      <summary className="cursor-pointer px-3 py-2 text-[12.5px] font-semibold bg-secondary/50 flex items-center gap-2">
                        <span className="flex-1">
                          📋 {typeNames[typeId] || t.name}
                          <span className="ml-2 text-muted-foreground font-normal text-[11px]">
                            ID: {typeId} — {steps.length} Schritte, {enabledCount} aktiv
                          </span>
                        </span>
                      </summary>
                      <div className="p-3 space-y-2">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => bulkSetEnabled(typeId, t, true)}
                            className="px-2 py-1 text-[11.5px] rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                          >
                            Alle aktivieren
                          </button>
                          <button
                            type="button"
                            onClick={() => bulkSetEnabled(typeId, t, false)}
                            className="px-2 py-1 text-[11.5px] rounded bg-red-100 text-red-700 hover:bg-red-200"
                          >
                            Alle deaktivieren
                          </button>
                        </div>
                        <table className="w-full text-[12px]">
                          <thead>
                            <tr className="text-left text-muted-foreground text-[10.5px] uppercase tracking-wider">
                              <th style={{ width: 40, textAlign: "center" }}>Aktiv</th>
                              <th>Schritt</th>
                              <th style={{ width: 60, textAlign: "center" }}>Code</th>
                              <th style={{ width: 140 }}>Schwellenwert</th>
                              <th style={{ width: 110 }}>Priorität</th>
                            </tr>
                          </thead>
                          <tbody>
                            {steps.map((s) => {
                              const r = getOrDefaultRule(typeId, s)
                              return (
                                <tr key={s.id} style={{ opacity: r.enabled ? 1 : 0.55 }} className="border-t">
                                  <td style={{ textAlign: "center", padding: "6px 4px" }}>
                                    <input
                                      type="checkbox"
                                      checked={r.enabled}
                                      onChange={(e) => upsertRule(typeId, s, { enabled: e.target.checked })}
                                    />
                                  </td>
                                  <td style={{ padding: "6px 4px" }}>{s.name}</td>
                                  <td style={{ textAlign: "center", padding: "6px 4px", color: "#64748b" }}>
                                    {(s as any).code ?? ""}
                                  </td>
                                  <td style={{ padding: "6px 4px", whiteSpace: "nowrap" }}>
                                    <input
                                      type="number" min={1}
                                      value={r.threshold}
                                      onChange={(e) => upsertRule(typeId, s, { threshold: parseInt(e.target.value) || 1 })}
                                      style={{ width: 55, padding: "2px 4px", border: "1px solid #e2e8f0", borderRadius: 4 }}
                                    />
                                    <select
                                      value={r.unit}
                                      onChange={(e) => upsertRule(typeId, s, { unit: e.target.value as "h" | "d" })}
                                      style={{ width: 70, marginLeft: 4, padding: "2px 4px", border: "1px solid #e2e8f0", borderRadius: 4 }}
                                    >
                                      <option value="h">Std</option>
                                      <option value="d">Tage</option>
                                    </select>
                                  </td>
                                  <td style={{ padding: "6px 4px" }}>
                                    <select
                                      value={r.priority}
                                      onChange={(e) => upsertRule(typeId, s, { priority: e.target.value as PipelineRule["priority"] })}
                                      style={{ width: "100%", padding: "2px 4px", border: "1px solid #e2e8f0", borderRadius: 4 }}
                                    >
                                      <option value="low">niedrig</option>
                                      <option value="medium">mittel</option>
                                      <option value="high">hoch</option>
                                      <option value="critical">kritisch</option>
                                    </select>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}
    </Section>
  )
}

// FwdRule + ForwardingRulesEditor entfernt (C2b) — Forward-Rules werden im Kontext-Center gepflegt.

// DocTriggersEditor entfernt — die Konfiguration liegt jetzt im Kontext-Center
// → Tab "Pipeline" → Card "Status nach Dokument-Erstellung" pro Projekttyp.

function F({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-muted-foreground block mb-1">{label}</label>
      {children}
      {hint && <div className="text-[10.5px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  )
}
